import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../api';

// ── Design tokens ──────────────────────────────────────────────────────────
const NAVY   = '#1e3a4a';
const YELLOW = '#FFD54F';

// ── Account structure: Category → Sub-accounts (mirrors backend) ───────────
const ACCOUNTS = {
  Income: {
    color: '#16a34a',
    items: [
      { account: 'Customer Payment',      hasGst: true  },
      { account: 'Business Revenue',    hasGst: true  },
      { account: 'Other Income',         hasGst: true  },
      { account: 'GST-Free Income',      hasGst: false },
    ],
  },
  Expense: {
    color: '#dc2626',
    items: [
      { account: 'Staff / Payroll',      hasGst: false },
      { account: 'Fuel',                 hasGst: true  },
      { account: 'Chemicals & Supplies', hasGst: true  },
      { account: 'Equipment & Tools',    hasGst: true  },
      { account: 'Rent & Premises',      hasGst: true  },
      { account: 'Insurance',            hasGst: true  },
      { account: 'Phone & Internet',     hasGst: true  },
      { account: 'Marketing',            hasGst: true  },
      { account: 'Software',             hasGst: true  },
      { account: 'Vehicle Expenses',     hasGst: true  },
      { account: 'Bank Fees',            hasGst: false },
      { account: 'Tax / BAS Payment',    hasGst: false },
      { account: 'Expense Refund',       hasGst: true  },
      { account: 'Other Expenses',       hasGst: true  },
    ],
  },
  Transfer: {
    color: '#6b7280',
    items: [
      { account: 'Transfer In',          hasGst: false },
      { account: 'Transfer Out',         hasGst: false },
      { account: 'Owner Drawings',       hasGst: false },
      { account: 'Owner Contribution',   hasGst: false },
      { account: 'GST Reserve',          hasGst: false },
      { account: 'Tax Reserve',          hasGst: false },
      { account: 'Donation',      hasGst: false },
    ],
  },
};

// ── Account-level row accent colours ──────────────────────────────────────
const ACCOUNT_ROW_COLORS = {
  'Staff / Payroll': { bg: '#f5f3ff', border: '#7c3aed' },  // purple
  'Owner Drawings':  { bg: '#fefce8', border: '#ca8a04' },  // yellow
  'Donation':        { bg: '#eff6ff', border: '#2563eb' },  // blue
};

// Flat lookup: account name → { category, hasGst }
const ACCOUNT_META = {};
Object.entries(ACCOUNTS).forEach(([cat, { items }]) => {
  items.forEach(({ account, hasGst }) => {
    ACCOUNT_META[account] = { category: cat, hasGst };
  });
});

function defaultGst(account) { return ACCOUNT_META[account]?.hasGst ?? false; }
function accountCategory(account) { return ACCOUNT_META[account]?.category ?? 'Expense'; }

// ── Auto-categorise by keyword ─────────────────────────────────────────────
const KEYWORD_MAP = [
  { cat: 'Expense', acc: 'Fuel',                  gst: true,  kw: ['fuel','shell','bp ','caltex','ampol','7-eleven','7eleven','united petro','puma'] },
  { cat: 'Expense', acc: 'Chemicals & Supplies',  gst: true,  kw: ['aldi','woolworths','coles','iga ','autobarn','repco','supercheap','bunnings','meguiar','gtechniq','carpro'] },
  { cat: 'Expense', acc: 'Equipment & Tools',     gst: true,  kw: ['harvey norm','jb hi','officework','kmart','target','equipment','vacuum','polisher'] },
  { cat: 'Expense', acc: 'Rent & Premises',       gst: true,  kw: ['rent','lease','premises','commercial','real estate'] },
  { cat: 'Expense', acc: 'Insurance',             gst: true,  kw: ['insurance','allianz','nrma','racwa','guild','aami','suncorp','qbe'] },
  { cat: 'Expense', acc: 'Phone & Internet',      gst: true,  kw: ['telstra','optus','vodafone','amaysim','belong','aussie broadband','tpg','iinet'] },
  { cat: 'Expense', acc: 'Marketing',             gst: true,  kw: ['facebook','meta ','google','instagram','canva','advertising','marketing'] },
  { cat: 'Expense', acc: 'Tax / BAS Payment',     gst: false, kw: ['ato ','australian tax','bas payment','tax office'] },
  // Refunds / returns — credit on an Expense category. computeBAS treats
  // these as contra-expenses so the refund offsets the original expense
  // (and the GST credit) instead of inflating Income.
  { cat: 'Expense', acc: 'Expense Refund',        gst: true,  kw: ['return ','refund','reversal','credit aliexpress','aliexpress north sydney'] },
  // Staff payments: "Transfer To Rafael Oliveira Week 14" — staff names checked BEFORE generic Transfer Out
  { cat: 'Expense', acc: 'Staff / Payroll',        gst: false, kw: ['staff','wage','salary','payroll','angela','jose ','patricia','rafael','fellipe','gabriel','caua'] },
  // Savings accounts — must come BEFORE generic Transfer Out
  { cat: 'Transfer',acc: 'Tax Reserve',           gst: false, kw: ['acts tax','acts-tax','tax saving','actstax'] },
  { cat: 'Transfer',acc: 'GST Reserve',           gst: false, kw: ['gst saving','gst-saving','gst reserve','gstsaving'] },
  { cat: 'Transfer',acc: 'Donation',        gst: false, kw: ['wise','donation'] },
  { cat: 'Transfer',acc: 'Owner Drawings',        gst: false, kw: ['personal','draw','withdrawal','michel carrascoza','michel oliveira'] },
  { cat: 'Transfer',acc: 'Transfer Out',          gst: false, kw: ['transfer to','netbank'] },
  { cat: 'Transfer',acc: 'Transfer In',           gst: false, kw: ['transfer from','erika','fast transfer'] },
  { cat: 'Income',  acc: 'Customer Payment',       gst: true,  kw: ['melville','holden','motor'] },
  { cat: 'Income',  acc: 'Business Revenue',      gst: true,  kw: ['receipt','revenue','payment receive','commbank app gst','commbank app tax'] },
];

function autoAccount(description, amount) {
  const d = (description || '').toLowerCase();
  // "Transfer To [Any Name] Week N" → always Staff / Payroll
  if (/transfer to .+ week\s*\d/i.test(d)) return { category: 'Expense', account: 'Staff / Payroll', has_gst: false };
  for (const { cat, acc, gst, kw } of KEYWORD_MAP) {
    if (kw.some(k => d.includes(k))) return { category: cat, account: acc, has_gst: gst };
  }
  if (amount > 0) return { category: 'Transfer', account: 'Transfer In',    has_gst: false };
  return              { category: 'Expense',  account: 'Other Expenses', has_gst: true  };
}

// ── Parser ─────────────────────────────────────────────────────────────────
function parseAmount(raw) {
  if (raw == null || raw === '') return null;
  const s = String(raw).replace(/\$|,|\s/g, '');
  const n = parseFloat(s);
  return isNaN(n) ? null : n;
}

function parseDate(raw) {
  if (!raw) return null;
  const s = raw.trim();
  const dmy = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
  if (dmy) {
    const [, d, m, y] = dmy;
    return `${y.length === 2 ? '20'+y : y}-${m.padStart(2,'0')}-${d.padStart(2,'0')}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return null;
}

function parseBankText(rawText) {
  if (!rawText?.trim()) return [];
  const transactions = [];
  for (const line of rawText.split('\n').map(l => l.trim()).filter(Boolean)) {
    const parts = line.split('\t').map(p => p.trim());
    if (parts.length >= 3) {
      const date = parseDate(parts[0]);
      const amt  = parseAmount(parts[1]);
      if (date && amt !== null) {
        const desc  = parts[2] || '';
        const bal   = parts[3] != null ? parseAmount(parts[3]) : null;
        const debit = amt < 0 ? Math.abs(amt) : 0;
        const credit= amt > 0 ? amt            : 0;
        transactions.push({ txn_date: date, description: desc, debit, credit, balance: bal, ...autoAccount(desc, amt), notes: '' });
        continue;
      }
    }
    const csv = line.split(',').map(p => p.trim());
    if (csv.length >= 4) {
      const date = parseDate(csv[0]);
      if (date) {
        const desc  = csv[1] || '';
        const debit = parseAmount(csv[2]);
        const credit= parseAmount(csv[3]);
        const bal   = csv[4] ? parseAmount(csv[4]) : null;
        if (desc.toLowerCase() !== 'description' && (debit !== null || credit !== null)) {
          const netAmt = (credit||0) - (debit||0);
          transactions.push({ txn_date: date, description: desc, debit: debit||0, credit: credit||0, balance: bal, ...autoAccount(desc, netAmt), notes: '' });
        }
      }
    }
  }
  return transactions;
}

// ── Helpers ────────────────────────────────────────────────────────────────
function fmt(n) { return (Number(n) || 0).toFixed(2); }
function fmtDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

// Freshness label for the Zoho source badge: "14:30" if today, else "02/07 14:30".
function fmtFetchedAt(ts) {
  const d = new Date(ts);
  if (isNaN(d)) return '';
  const hm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay ? hm : `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} ${hm}`;
}

// ── ISO week helpers ───────────────────────────────────────────────────────
function getISOWeekInfo(dateStr) {
  if (!dateStr) return { year: 0, week: 0, weekStart: '', weekEnd: '' };
  const d   = new Date(dateStr + 'T00:00:00');
  const day = (d.getDay() + 6) % 7;
  const mon = new Date(d); mon.setDate(d.getDate() - day);
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  const tmp = new Date(mon); tmp.setDate(tmp.getDate() + 4 - (tmp.getDay() || 7));
  const week = Math.ceil((((tmp - new Date(tmp.getFullYear(), 0, 1)) / 86400000) + 1) / 7);
  const toISO = dt => `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
  return { year: mon.getFullYear(), week, weekStart: toISO(mon), weekEnd: toISO(sun) };
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function weekRangeLabel(ws, we) {
  if (!ws) return '';
  const [,ms,ds] = ws.split('-'); const [,me,de] = we.split('-');
  return ms === me ? `${ds}–${de} ${MONTHS[+ms-1]}` : `${ds} ${MONTHS[+ms-1]} – ${de} ${MONTHS[+me-1]}`;
}

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function groupByMonthAndWeek(txns) {
  const months = {};
  for (const t of txns) {
    if (!t.txn_date) continue;
    const [y, m] = t.txn_date.split('-');
    const monthKey = `${y}-${m}`;
    if (!months[monthKey]) months[monthKey] = { monthKey, year: parseInt(y), month: parseInt(m), weeks: {} };
    const info = getISOWeekInfo(t.txn_date);
    const weekKey = `${info.year}-W${String(info.week).padStart(2,'0')}`;
    if (!months[monthKey].weeks[weekKey]) months[monthKey].weeks[weekKey] = { key: weekKey, ...info, txns: [] };
    months[monthKey].weeks[weekKey].txns.push(t);
  }
  return Object.values(months)
    .sort((a, b) => b.monthKey.localeCompare(a.monthKey))
    .map(mo => ({ ...mo, weeks: Object.values(mo.weeks).sort((a, b) => b.week - a.week) }));
}

function groupStatementsByYear(stmts) {
  const map = {};
  for (const s of stmts) {
    const yr = (s.period_label||'').match(/\b(20\d{2})\b/)?.[1]
      || s.week_start?.slice(0,4)
      || new Date(s.created_at).getFullYear().toString();
    if (!map[yr]) map[yr] = [];
    map[yr].push(s);
  }
  return Object.entries(map).sort((a,b) => b[0].localeCompare(a[0]));
}

// ── BAS Summary computation ────────────────────────────────────────────────
function computeBAS(transactions) {
  // income
  const income = {};      // account → { ex_gst, gst_amount }
  let totalIncomeEx = 0, gstCollected = 0;

  // expense
  const expense = {};     // account → { amount, gst_amount }
  let totalExpense = 0, gstCredits = 0;

  // transfer
  const transfer = {};    // account → { in, out }

  // Helper: produce one or more "BAS rows" from a single transaction. If the
  // transaction has been itemised (is_split + splits[]), each split is its
  // own logical row with its own amount / category / GST. If not, the whole
  // debit/credit lands on the parent's category — the original behaviour.
  function expandToRows(t) {
    const debit  = Number(t.debit)  || 0;
    const credit = Number(t.credit) || 0;
    if (t.is_split && Array.isArray(t.splits) && t.splits.length > 0) {
      // The sign of each split mirrors the parent (a refund split contributes
      // a credit, a normal split contributes a debit). Splits are stored as
      // positive amounts; we re-attach the direction here.
      const direction = credit > 0 ? 'credit' : 'debit';
      return t.splits.map(s => ({
        category: s.category, account: s.account, has_gst: s.has_gst,
        debit:  direction === 'debit'  ? (parseFloat(s.amount) || 0) : 0,
        credit: direction === 'credit' ? (parseFloat(s.amount) || 0) : 0,
      }));
    }
    return [{ category: t.category, account: t.account, has_gst: t.has_gst, debit, credit }];
  }

  // Flatten transactions into per-line BAS rows. Each row is processed by
  // the existing income/expense/transfer branches below.
  const flat = [];
  for (const t of transactions) for (const r of expandToRows(t)) flat.push(r);

  for (const t of flat) {
    const cat    = t.category || 'Expense';
    const acc    = t.account  || 'Other Expenses';
    const hasGst = t.has_gst ?? defaultGst(acc);
    const debit  = Number(t.debit)  || 0;
    const credit = Number(t.credit) || 0;

    if (cat === 'Income') {
      const ex  = hasGst ? credit / 1.1 : credit;
      const gst = hasGst ? credit - ex  : 0;
      if (!income[acc]) income[acc] = { ex_gst: 0, gst_amount: 0, hasGst };
      income[acc].ex_gst     += ex;
      income[acc].gst_amount += gst;
      totalIncomeEx += ex;
      gstCollected  += gst;
    } else if (cat === 'Expense') {
      // Treat a credit on an Expense row as a contra-expense (refund/reversal):
      // it reduces both the dollar amount and the GST credit you previously
      // claimed. The dedicated "Expense Refund" sub-account is just the
      // canonical home for these — the math also works for a reversal that
      // accidentally landed in any other Expense bucket.
      const isRefund = credit > 0 && debit === 0;
      const signedAmt = isRefund ? -credit : debit;
      const ex  = hasGst ? signedAmt / 1.1 : signedAmt;
      const gst = hasGst ? signedAmt - ex  : 0;
      if (!expense[acc]) expense[acc] = { amount: 0, ex_gst: 0, gst_amount: 0, hasGst };
      expense[acc].amount    += signedAmt;
      expense[acc].ex_gst    += ex;
      expense[acc].gst_amount+= gst;
      totalExpense += signedAmt;
      gstCredits   += gst;
    } else {
      if (!transfer[acc]) transfer[acc] = { in: 0, out: 0 };
      transfer[acc].in  += credit;
      transfer[acc].out += debit;
    }
  }

  const netGst    = gstCollected - gstCredits;
  const netProfit = totalIncomeEx - totalExpense;
  return { income, expense, transfer, totalIncomeEx, gstCollected, totalExpense, gstCredits, netGst, netProfit };
}

// ── Dynamic Dashboard helpers ──────────────────────────────────────────────
// Australian FY runs Jul 1 → Jun 30. This business reports BAS MONTHLY:
// BAS for month M is due on the 21st of month M+1.
// Example: April BAS due 21 May, May BAS due 21 June, etc.

function lastDayOfMonth(year, month1) {
  return new Date(year, month1, 0).getDate();   // month1 is 1-based
}
function shiftYearISO(iso, delta) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${parseInt(y) + delta}-${m}-${d}`;
}
function addDaysISO(iso, days) {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function getCurrentFY(today = new Date()) {
  const y = today.getFullYear();
  const m = today.getMonth() + 1;
  const start = m >= 7 ? y : y - 1;
  return {
    start: `${start}-07-01`,
    end:   `${start + 1}-06-30`,
    label: `FY ${start}–${String(start + 1).slice(2)}`,
  };
}
function getPrevFY(today = new Date()) {
  const cur = getCurrentFY(today);
  const startY = parseInt(cur.start.slice(0, 4)) - 1;
  return {
    start: `${startY}-07-01`,
    end:   `${startY + 1}-06-30`,
    label: `FY ${startY}–${String(startY + 1).slice(2)}`,
  };
}

/** Monthly BAS period being reported on right now.
 *  Up to and including the 21st of month M, you're still on the hook for the
 *  PREVIOUS month's BAS (due on the 21st of M). After the 21st, the next BAS
 *  to lodge is the current month's, which is due on the 21st of M+1.
 */
function getCurrentBasPeriod(today = new Date()) {
  const y = today.getFullYear();
  const m = today.getMonth() + 1;       // 1-based current calendar month
  const d = today.getDate();

  // The BAS period we're reporting is M-1 if today <= 21st of M (still owed),
  // otherwise M (current month, due next month).
  let py = y, pm = m - 1;
  if (d > 21) { py = y; pm = m; }
  if (pm <= 0) { pm += 12; py -= 1; }

  const start = `${py}-${String(pm).padStart(2,'0')}-01`;
  const end   = `${py}-${String(pm).padStart(2,'0')}-${lastDayOfMonth(py, pm)}`;
  // Due date: 21st of the month AFTER pm
  let dy = py, dm = pm + 1;
  if (dm > 12) { dm = 1; dy += 1; }
  const due   = `${dy}-${String(dm).padStart(2,'0')}-21`;
  return {
    start, end, due,
    year: py, month: pm,
    label: `${MONTH_NAMES[pm - 1]} ${py}`,
    range: MONTH_NAMES[pm - 1],
  };
}

function fmtMoney0(n) {
  const v = Math.round(Number(n) || 0);
  return '$' + v.toLocaleString('en-AU');
}

function KpiCard({ label, value, sub, subColor = '#16a34a' }) {
  return (
    <div style={{
      background: '#F7F7F2', borderRadius: 12, padding: '10px 12px',
      display: 'flex', flexDirection: 'column', gap: 2, minHeight: 76,
      minWidth: 0, // allow grid cells to shrink so 8-up works on narrower screens
    }}>
      <div style={{
        fontSize: 10.5, color: '#475569', fontWeight: 600,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }} title={label}>{label}</div>
      <div style={{
        fontSize: 18, fontWeight: 800, color: '#0F172A', letterSpacing: -0.3, lineHeight: 1.1,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }} title={value}>
        {value}
      </div>
      {sub && (
        <div style={{
          fontSize: 9.5, color: subColor, fontWeight: 600,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }} title={sub}>{sub}</div>
      )}
    </div>
  );
}

function DynamicDashboard({ allTxns, onTransactionUpdated }) {
  const [period, setPeriod] = React.useState('fy');         // 'fy' | 'q' | 'month' | 'lastfy' | 'custom'
  const [customStart, setCustomStart] = React.useState('');
  const [customEnd,   setCustomEnd]   = React.useState('');

  // Zoho P&L for the picked range — source of truth for revenue / expenses /
  // net profit / tax estimate. Falls back to the local bank-statement based
  // computeBAS when Zoho isn't configured or the call fails.
  const [zohoPl,      setZohoPl]      = React.useState(null);
  const [zohoPrevPl,  setZohoPrevPl]  = React.useState(null); // comparison period
  const [zohoLoading, setZohoLoading] = React.useState(false);
  const [zohoError,   setZohoError]   = React.useState(null);

  const today = React.useMemo(() => new Date(), []);
  const todayIso = today.toISOString().slice(0, 10);

  // Anchors that don't depend on the picked period
  const currentFY  = React.useMemo(() => getCurrentFY(today),  [today]);
  const prevFY     = React.useMemo(() => getPrevFY(today),     [today]);
  const basPeriod  = React.useMemo(() => getCurrentBasPeriod(today), [today]);

  // Resolve the active range + a comparison range
  const { range, prevRange, label } = React.useMemo(() => {
    if (period === 'fy')     return { range: currentFY, prevRange: prevFY, label: currentFY.label };
    if (period === 'lastfy') {
      const startY = parseInt(prevFY.start.slice(0, 4)) - 1;
      return {
        range: prevFY,
        prevRange: { start: `${startY}-07-01`, end: `${startY + 1}-06-30` },
        label: prevFY.label,
      };
    }
    if (period === 'q') {
      return {
        range: basPeriod,
        prevRange: { start: shiftYearISO(basPeriod.start, -1), end: shiftYearISO(basPeriod.end, -1) },
        label: basPeriod.label,
      };
    }
    if (period === 'month') {
      const y = today.getFullYear(), m = today.getMonth() + 1;
      const start = `${y}-${String(m).padStart(2,'0')}-01`;
      const end   = `${y}-${String(m).padStart(2,'0')}-${lastDayOfMonth(y, m)}`;
      return {
        range: { start, end },
        prevRange: { start: shiftYearISO(start, -1), end: shiftYearISO(end, -1) },
        label: `${MONTH_NAMES[m - 1]} ${y}`,
      };
    }
    // custom
    if (customStart && customEnd) {
      return { range: { start: customStart, end: customEnd }, prevRange: null, label: `${fmtDate(customStart)} – ${fmtDate(customEnd)}` };
    }
    return { range: null, prevRange: null, label: 'Pick dates' };
  }, [period, currentFY, prevFY, basPeriod, customStart, customEnd, today]);

  // Filter + compute BAS for the active range and the comparison range
  const periodBas = React.useMemo(() => {
    if (!range) return null;
    const t = allTxns.filter(x => x.txn_date && x.txn_date >= range.start && x.txn_date <= range.end);
    return computeBAS(t);
  }, [allTxns, range]);

  const prevBas = React.useMemo(() => {
    if (!prevRange) return null;
    const t = allTxns.filter(x => x.txn_date && x.txn_date >= prevRange.start && x.txn_date <= prevRange.end);
    return computeBAS(t);
  }, [allTxns, prevRange]);

  // BAS card (independent of selected period — always shows the month
  // currently being reported on; due date is the 21st of the next month).
  const quarterBas = React.useMemo(() => {
    const t = allTxns.filter(x => x.txn_date && x.txn_date >= basPeriod.start && x.txn_date <= basPeriod.end);
    return computeBAS(t);
  }, [allTxns, basPeriod]);

  // Pull P&L from Zoho whenever the picked range changes. Backed by a 5-min
  // server-side cache so flipping between FY / month / quarter doesn't keep
  // hammering Zoho's report API. Also fires the prev-range request in
  // parallel so the YoY % on the Revenue card is apples-to-apples.
  const rangeKey     = range     ? `${range.start}|${range.end}` : '';
  const prevRangeKey = prevRange ? `${prevRange.start}|${prevRange.end}` : '';
  React.useEffect(() => {
    if (!range) { setZohoPl(null); setZohoPrevPl(null); return; }
    let cancelled = false;
    setZohoLoading(true);
    setZohoError(null);
    Promise.allSettled([
      api.zohoPlReport(range.start, range.end),
      prevRange ? api.zohoPlReport(prevRange.start, prevRange.end) : Promise.resolve(null),
    ]).then(([cur, prv]) => {
      if (cancelled) return;
      if (cur.status === 'fulfilled') {
        setZohoPl(cur.value);
        setZohoError(null);
      } else {
        setZohoPl(null);
        const msg = (cur.reason && cur.reason.message) || 'Failed to load Zoho P&L';
        setZohoError(/not configured/i.test(msg) ? 'Zoho not configured' : msg);
      }
      if (prv && prv.status === 'fulfilled') setZohoPrevPl(prv.value);
      else                                   setZohoPrevPl(null);
      setZohoLoading(false);
    });
    return () => { cancelled = true; };
  }, [rangeKey, prevRangeKey]);

  // BAS due-date status
  const daysUntilDue = Math.floor((new Date(basPeriod.due + 'T00:00:00') - new Date(todayIso + 'T00:00:00')) / 86400000);
  const dueLabel = daysUntilDue > 0
    ? `Due in ${daysUntilDue} day${daysUntilDue === 1 ? '' : 's'}`
    : daysUntilDue === 0
      ? 'Due today'
      : `Overdue by ${-daysUntilDue} day${-daysUntilDue === 1 ? '' : 's'}`;
  const dueBadgeBg = daysUntilDue > 7 ? '#FEF3C7' : daysUntilDue > 0 ? '#FED7AA' : '#FECACA';
  const dueBadgeFg = daysUntilDue > 7 ? '#92400E' : daysUntilDue > 0 ? '#9A3412' : '#991B1B';

  if (!periodBas) {
    return (
      <DashboardShell
        period={period} setPeriod={setPeriod}
        currentFY={currentFY} basPeriod={basPeriod}
        customStart={customStart} setCustomStart={setCustomStart}
        customEnd={customEnd} setCustomEnd={setCustomEnd}
      >
        <div style={{ padding: '32px 12px', color: '#94a3b8', fontSize: 13, textAlign: 'center' }}>
          {period === 'custom' ? 'Pick a start and end date.' : 'No data for this period.'}
        </div>
      </DashboardShell>
    );
  }

  // KPI metrics — when Zoho's P&L is available, it's the source of truth
  // for revenue / expenses / net profit (GST handled correctly, no double-
  // counting from sub-account credits). Fall back to local computeBAS so the
  // dashboard still works when Zoho isn't configured or is down.
  const useZoho   = !!zohoPl;
  const revenue   = useZoho ? zohoPl.revenue       : periodBas.totalIncomeEx;
  const gst       = periodBas.gstCollected;   // BAS GST stays local for now
  const netProfit = useZoho ? zohoPl.net_profit    : periodBas.netProfit;
  const taxRate   = 0.25;
  const tax       = Math.max(0, netProfit * taxRate);
  const margin    = revenue > 0 ? (netProfit / revenue) * 100 : 0;
  const gstPctOfSales = revenue > 0 ? (gst / revenue) * 100 : 0;
  // YoY: prefer Zoho-to-Zoho (apples-to-apples), fall back to local-to-local.
  // Don't mix sources — that would compare ex-GST Zoho revenue to ex-GST
  // local revenue (potentially double-counted) and look meaningless.
  const prevRevenue = useZoho
    ? (zohoPrevPl ? zohoPrevPl.revenue : null)
    : (prevBas    ? prevBas.totalIncomeEx : null);
  const yoy = prevRevenue && prevRevenue > 0
    ? ((revenue - prevRevenue) / prevRevenue) * 100
    : null;

  // Detail breakdowns — pull from the buckets computeBAS already filled in,
  // so each card reads from the same source the totals do (no drift).
  const expByAcc = periodBas.expense || {};
  const trByAcc  = periodBas.transfer || {};
  const sumExpense = (acc) => (expByAcc[acc]?.amount || 0); // amount inc GST
  const sumTransferOut = (acc) => (trByAcc[acc]?.out || 0);

  const staffPayroll  = sumExpense('Staff / Payroll');
  const materials     = sumExpense('Chemicals & Supplies') + sumExpense('Equipment & Tools');
  // Headline "Total expenses" + the tax breakdown use Zoho when available.
  // The per-bucket percentages (Staff %, Materials %) keep using the local
  // total so the percentages remain internally consistent with the cards.
  const totalExpense       = useZoho ? zohoPl.total_expense : periodBas.totalExpense;
  const totalExpenseLocal  = periodBas.totalExpense;
  const donations     = sumTransferOut('Donation');
  const gstReserve    = sumTransferOut('GST Reserve');
  const taxReserve    = sumTransferOut('Tax Reserve');
  const ownerDrawings = sumTransferOut('Owner Drawings');
  const pctOfExpense = (n) => totalExpenseLocal > 0 ? (n / totalExpenseLocal) * 100 : 0;

  // BAS form line items for the quarter
  const G1 = quarterBas.totalIncomeEx + quarterBas.gstCollected; // total sales inc GST
  const A1 = quarterBas.gstCollected;
  const B1 = quarterBas.gstCredits;
  const netGst = A1 - B1;

  return (
    <DashboardShell
      period={period} setPeriod={setPeriod}
      currentFY={currentFY} basPeriod={basPeriod}
      customStart={customStart} setCustomStart={setCustomStart}
      customEnd={customEnd} setCustomEnd={setCustomEnd}
    >
      {/* Data-source pill — tells the boss whether the headline numbers
          (Revenue / Net profit / Tax / Total expenses) came from Zoho
          Books (correct, GST-handled) or fell back to the local bank-
          statement based computeBAS (which has the sub-account double-
          counting issue). The 4 breakdown cards (Staff / Materials /
          Donations) always read from local bank-statement data. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        {zohoLoading ? (
          <div style={{ background: '#F1F5F9', color: '#475569', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999 }}>
            ⏳ Loading P&L from Zoho…
          </div>
        ) : useZoho && zohoPl.stale ? (
          <div
            title="Zoho Books is unreachable right now — showing its most recent snapshot (still Zoho's real numbers, just not live)."
            style={{ background: '#FEF3C7', color: '#92400E', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999 }}
          >
            📊 Zoho Books (last snapshot{zohoPl.fetched_at ? ` · ${fmtFetchedAt(zohoPl.fetched_at)}` : ''}) — live fetch failed
          </div>
        ) : useZoho ? (
          <div
            title="Revenue / Net profit / Tax / Total expenses come straight from Zoho Books — the same numbers your accountant sees."
            style={{ background: '#DCFCE7', color: '#15803D', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999 }}
          >
            📊 Source: Zoho Books{zohoPl.fetched_at ? ` · updated ${fmtFetchedAt(zohoPl.fetched_at)}` : ''}
          </div>
        ) : (
          <div
            title={zohoError || 'Zoho P&L unavailable — showing locally-computed figures from imported bank statements. These are approximate and can differ from the accountant’s numbers.'}
            style={{ background: '#FEE2E2', color: '#991B1B', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999 }}
          >
            ⚠ Local approximation {zohoError ? `· ${zohoError}` : ''}
          </div>
        )}
      </div>

      {/* KPI cards — single row of 8 compact cards (4 top-line metrics +
          4 expense breakdowns). Wraps gracefully when the viewport is too
          narrow to fit all 8 side-by-side. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 12 }}>
        <KpiCard
          label={`Revenue (ex GST)${period !== 'fy' ? ` · ${label}` : ''}`}
          value={fmtMoney0(revenue)}
          sub={yoy != null
            ? `${yoy >= 0 ? '▲' : '▼'} ${Math.abs(yoy).toFixed(0)}% vs prior`
            : null}
          subColor={yoy != null ? (yoy >= 0 ? '#16a34a' : '#dc2626') : '#9ca3af'}
        />
        <KpiCard
          label="GST collected"
          value={fmtMoney0(gst)}
          sub={`${gstPctOfSales.toFixed(0)}% of sales`}
          subColor="#64748b"
        />
        <KpiCard
          label="Net profit"
          value={fmtMoney0(netProfit)}
          sub={`${margin.toFixed(0)}% margin`}
          subColor={netProfit >= 0 ? '#16a34a' : '#dc2626'}
        />
        <KpiCard
          label="Tax estimated"
          value={fmtMoney0(tax)}
          sub="25% rate"
          subColor="#92400E"
        />
        <KpiCard
          label="Total expenses"
          value={fmtMoney0(totalExpense)}
          sub={revenue > 0 ? `${((totalExpense / revenue) * 100).toFixed(0)}% of revenue` : null}
          subColor="#dc2626"
        />
        <KpiCard
          label="Staff / Payroll"
          value={fmtMoney0(staffPayroll)}
          sub={totalExpense > 0 ? `${pctOfExpense(staffPayroll).toFixed(0)}% of exp` : null}
          subColor="#7c3aed"
        />
        <KpiCard
          label="Materials & Equip"
          value={fmtMoney0(materials)}
          sub={totalExpense > 0 ? `${pctOfExpense(materials).toFixed(0)}% of exp` : null}
          subColor="#0369A1"
        />
        <KpiCard
          label="Donations"
          value={fmtMoney0(donations)}
          sub="Supporting fund"
          subColor="#2563eb"
        />
      </div>

      {/* BAS card — monthly, due 21st of the following month */}
      <div style={{
        background: '#fff', border: '1px solid #E5E7EB', borderRadius: 16,
        padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#0F172A' }}>
            BAS — {basPeriod.label}
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8' }}>
            Due {fmtDate(basPeriod.due)}
          </div>
          <div style={{ marginLeft: 'auto', background: dueBadgeBg, color: dueBadgeFg, fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 999 }}>
            {dueLabel}
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
          <BasStat label="G1 Total sales"        value={fmtMoney0(G1)} />
          <BasStat label="1A GST on sales"       value={fmtMoney0(A1)} />
          <BasStat label="1B GST on purchases"   value={fmtMoney0(B1)} />
          <BasStat label="Net GST payable" value={fmtMoney0(netGst)} valueColor={netGst >= 0 ? '#991B1B' : '#16a34a'} />
        </div>
      </div>

      {/* Tax transparency — explains how the "Tax estimated" KPI is derived
          and clarifies which outflows come from post-tax money (donations,
          owner drawings) vs. just being parked in savings (GST/Tax reserves). */}
      <div style={{
        background: '#fff', border: '1px solid #E5E7EB', borderRadius: 16,
        padding: '18px 22px', marginTop: 12,
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24,
      }}>
        {/* Section 1: how tax is calculated */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#92400E', letterSpacing: 0.6, marginBottom: 10 }}>
            HOW TAX IS CALCULATED
          </div>
          <BreakdownRow label="Revenue (ex GST)"           value={fmtMoney0(revenue)} />
          <BreakdownRow label="− Deductible expenses"      value={`−${fmtMoney0(totalExpense)}`} valueColor="#dc2626" />
          <BreakdownRow label="= Taxable income"           value={fmtMoney0(netProfit)} bold borderTop />
          <BreakdownRow label={`× ${(taxRate * 100).toFixed(0)}% company rate`} value="" muted />
          <BreakdownRow label="= Tax estimated"            value={fmtMoney0(tax)} bold valueColor="#92400E" borderTop />
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8, lineHeight: 1.5 }}>
            Donations and reserve transfers are <strong>not</strong> deductible — they don't reduce taxable income.
          </div>
        </div>

        {/* Section 2: what gets paid from post-tax money */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#1e40af', letterSpacing: 0.6, marginBottom: 10 }}>
            ALSO PAID FROM POST-TAX
          </div>
          <BreakdownRow label="Donations"                  value={fmtMoney0(donations)} valueColor="#2563eb" />
          <BreakdownRow label="GST reserve (savings)"      value={fmtMoney0(gstReserve)} muted />
          <BreakdownRow label="Tax reserve (savings)"      value={fmtMoney0(taxReserve)} muted />
          {ownerDrawings > 0 && (
            <BreakdownRow label="Owner drawings" value={fmtMoney0(ownerDrawings)} muted />
          )}
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8, lineHeight: 1.5 }}>
            Reserves are <em>your own money</em> moved to savings to cover upcoming GST/Tax — not an expense.
          </div>
        </div>
      </div>

      {/* Phase-2 reconciliation card — Zoho vs local + suspicious-txn cleanup.
          Sits below the tax breakdown so the diff (and fix workflow) is
          immediately visible whenever the picked period changes. */}
      <ZohoComparisonCard
        range={range}
        onTransactionUpdated={onTransactionUpdated}
      />
    </DashboardShell>
  );
}

function BreakdownRow({ label, value, valueColor = '#0F172A', bold = false, muted = false, borderTop = false }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      padding: '6px 0',
      borderTop: borderTop ? '1px solid #E5E7EB' : 'none',
      marginTop: borderTop ? 4 : 0,
    }}>
      <span style={{ fontSize: 13, color: muted ? '#94a3b8' : '#475569', fontWeight: bold ? 700 : 500 }}>
        {label}
      </span>
      <span style={{ fontSize: bold ? 16 : 14, color: valueColor, fontWeight: bold ? 800 : 600 }}>
        {value}
      </span>
    </div>
  );
}

// ── Phase-2 reconciliation card ─────────────────────────────────────────────
// Renders below the dashboard tax breakdown. Pulls the side-by-side Zoho-vs-
// local view from the backend (/api/zoho/pl-comparison), highlights the diff,
// and shows the top "suspicious" transactions — the credits sitting in
// reserve / supporting sub-accounts marked as Income. Each row has a one-
// click "Fix" button that PUTs the suggested Transfer-In category back to
// the server.
function ZohoComparisonCard({ range, onTransactionUpdated }) {
  const [data,    setData]    = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error,   setError]   = React.useState(null);
  const [busyIds, setBusyIds] = React.useState(new Set());
  const [hideIds, setHideIds] = React.useState(new Set()); // user-dismissed rows
  const [expanded, setExpanded] = React.useState(false);
  // Deeper investigation panel — opens on demand so the heavy SQL doesn't
  // run on every period change. State separate from `data` so the user can
  // collapse without losing the suspicious-list state.
  const [diagOpen,    setDiagOpen]    = React.useState(false);
  const [diagData,    setDiagData]    = React.useState(null);
  const [diagLoading, setDiagLoading] = React.useState(false);
  const [diagError,   setDiagError]   = React.useState(null);

  const rangeKey = range ? `${range.start}|${range.end}` : '';

  const fetchData = React.useCallback(async () => {
    if (!range) { setData(null); return; }
    setLoading(true);
    setError(null);
    try {
      const r = await api.zohoPlComparison(range.start, range.end);
      setData(r);
      // Reset dismissed rows when the period changes — they're scoped to a
      // single period, no point preserving them across FY / month switches.
      setHideIds(new Set());
    } catch (err) {
      setError(err.message || 'Failed to load comparison');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [rangeKey]); // eslint-disable-next-line

  React.useEffect(() => { fetchData(); }, [fetchData]);

  // Diagnostic refetch — fires when the panel is opened or the period
  // changes while it's already open. Stays closed by default.
  React.useEffect(() => {
    if (!diagOpen || !range) { return; }
    let cancelled = false;
    setDiagLoading(true);
    setDiagError(null);
    api.zohoIncomeDiag(range.start, range.end)
      .then(d => { if (!cancelled) setDiagData(d); })
      .catch(err => { if (!cancelled) { setDiagError(err.message || 'Failed'); setDiagData(null); } })
      .finally(() => { if (!cancelled) setDiagLoading(false); });
    return () => { cancelled = true; };
  }, [diagOpen, rangeKey]); // eslint-disable-next-line

  if (!range) return null;

  async function handleFix(row) {
    if (!row.suggested || !row.suggested.account) {
      alert('No suggested account for this row — open the statement and pick one manually.');
      return;
    }
    setBusyIds(prev => { const s = new Set(prev); s.add(row.id); return s; });
    try {
      await api.updateTransaction(row.statement_id, row.id, {
        category: row.suggested.category,
        account:  row.suggested.account,
        // Transfers are GST-free by definition; clear the flag so the
        // local computeBAS doesn't accidentally treat the new Transfer
        // as having an ex/inc-GST split.
        has_gst:  false,
      });
      // Hide the row locally so the boss sees progress without a refetch.
      setHideIds(prev => { const s = new Set(prev); s.add(row.id); return s; });
      if (onTransactionUpdated) onTransactionUpdated();
    } catch (err) {
      alert(`Failed to update: ${err.message || err}`);
    } finally {
      setBusyIds(prev => { const s = new Set(prev); s.delete(row.id); return s; });
    }
  }

  function handleSkip(row) {
    setHideIds(prev => { const s = new Set(prev); s.add(row.id); return s; });
  }

  const cardStyle = {
    background: '#fff', border: '1px solid #E5E7EB', borderRadius: 16,
    padding: '18px 22px', marginTop: 12,
  };

  if (loading && !data) {
    return (
      <div style={cardStyle}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#0F172A', marginBottom: 8 }}>
          🔍 Reconciliation — Zoho vs Local
        </div>
        <div style={{ fontSize: 12, color: '#94a3b8' }}>Loading comparison…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div style={cardStyle}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#0F172A', marginBottom: 8 }}>
          🔍 Reconciliation — Zoho vs Local
        </div>
        <div style={{ fontSize: 12, color: '#dc2626' }}>{error}</div>
      </div>
    );
  }
  if (!data) return null;

  const visibleRows = (data.suspicious_transactions || []).filter(r => !hideIds.has(r.id));
  const visibleTotal = visibleRows.reduce((s, r) => s + r.credit, 0);
  // How many rows to show by default (top of the list); "Show all" expands.
  const TOP_N = 8;
  const shown = expanded ? visibleRows : visibleRows.slice(0, TOP_N);

  const fmtDateShort = iso => iso ? iso.slice(8,10) + '/' + iso.slice(5,7) + '/' + iso.slice(2,4) : '';

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#0F172A' }}>
          🔍 Reconciliation — Zoho vs Local
        </div>
        <div style={{ fontSize: 11, color: '#94a3b8' }}>
          {range.start} → {range.end}
        </div>
      </div>

      {/* Three-column compare table */}
      <div style={{ overflowX: 'auto', marginBottom: 16 }}>
        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: '#64748B', fontSize: 11, fontWeight: 700, textAlign: 'right' }}>
              <th style={{ textAlign: 'left', padding: '4px 8px' }}></th>
              <th style={{ padding: '4px 8px' }}>ZOHO BOOKS</th>
              <th style={{ padding: '4px 8px' }}>LOCAL STATEMENT</th>
              <th style={{ padding: '4px 8px' }}>DIFF</th>
            </tr>
          </thead>
          <tbody>
            <CompareRow label="Revenue (ex GST)" zoho={data.zoho.revenue}       local={data.local.revenue_ex_gst}  />
            <CompareRow label="Expenses"         zoho={data.zoho.total_expense} local={data.local.expense_inc_gst} note="local is inc-GST" />
            <CompareRow label="Net profit"       zoho={data.zoho.net_profit}    local={data.local.net_profit} bold />
          </tbody>
        </table>
      </div>

      {/* Suspicious-transactions list */}
      <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: 14 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#92400E' }}>
            🚨 Credits in sub-accounts marked as <em>Income</em> ({visibleRows.length} rows · {fmtMoney0(visibleTotal)})
          </div>
          <div style={{ fontSize: 11, color: '#64748B' }}>
            These should usually be <strong>Transfer In</strong> from your main ACTS account
          </div>
        </div>

        {visibleRows.length === 0 ? (
          <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', color: '#15803D', borderRadius: 8, padding: 12, fontSize: 13, fontWeight: 600 }}>
            🎉 None left! All sub-account credits in this period look correctly categorised.
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {shown.map(row => {
                const busy = busyIds.has(row.id);
                return (
                  <div key={row.id} style={{
                    display: 'grid',
                    gridTemplateColumns: '1.1fr 2fr 1fr auto',
                    alignItems: 'center', gap: 10,
                    background: '#FFFBEB', border: '1px solid #FCD34D',
                    borderRadius: 8, padding: '8px 12px', fontSize: 12,
                  }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#92400E' }}>{row.bank_account_name}</div>
                      <div style={{ fontSize: 10, color: '#94a3b8' }}>{fmtDateShort(row.txn_date)}</div>
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                           title={row.description}>
                        {row.description || <em style={{ color: '#94a3b8' }}>(no description)</em>}
                      </div>
                      <div style={{ fontSize: 10, color: '#64748B' }}>
                        currently <strong>{row.category}</strong>{row.account ? ` / ${row.account}` : ''} → suggest <strong>{row.suggested.category}</strong>{row.suggested.account ? ` / ${row.suggested.account}` : ' (pick manually)'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', fontWeight: 700, color: '#92400E', fontSize: 13 }}>
                      {fmtMoney0(row.credit)}
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => handleFix(row)} disabled={busy || !row.suggested.account}
                        title={row.suggested.account ? `Re-categorize as ${row.suggested.category} / ${row.suggested.account}` : 'No suggested account — open statement to pick one'}
                        style={{
                          padding: '4px 10px', borderRadius: 6, border: 'none',
                          background: busy ? '#94a3b8' : (!row.suggested.account ? '#cbd5e1' : '#16a34a'),
                          color: '#fff', fontWeight: 700, fontSize: 11,
                          cursor: busy || !row.suggested.account ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap',
                        }}>
                        {busy ? '…' : '✓ Fix'}
                      </button>
                      <button onClick={() => handleSkip(row)} disabled={busy}
                        title="Hide this row — won't change the data, just removes it from this list"
                        style={{
                          padding: '4px 10px', borderRadius: 6, border: '1px solid #CBD5E1',
                          background: '#fff', color: '#475569', fontWeight: 600, fontSize: 11,
                          cursor: busy ? 'not-allowed' : 'pointer',
                        }}>
                        Skip
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {visibleRows.length > TOP_N && (
              <button onClick={() => setExpanded(e => !e)}
                style={{
                  marginTop: 10, padding: '6px 14px', borderRadius: 8,
                  border: '1px solid #CBD5E1', background: '#fff', color: '#475569',
                  fontWeight: 600, fontSize: 12, cursor: 'pointer',
                }}>
                {expanded ? `Show top ${TOP_N}` : `Show all ${visibleRows.length} rows`}
              </button>
            )}
          </>
        )}
      </div>

      {/* Investigate further — collapsed by default. Fires the diagnostic
          endpoint on open. Shows: Income totals by bank account, the 25
          largest Income credits, and any duplicate-looking groups. */}
      <div style={{ borderTop: '1px solid #E5E7EB', marginTop: 14, paddingTop: 12 }}>
        <button onClick={() => setDiagOpen(o => !o)}
          style={{
            padding: '6px 14px', borderRadius: 8,
            border: '1px solid #CBD5E1', background: diagOpen ? '#0F172A' : '#fff',
            color: diagOpen ? '#fff' : '#0F172A',
            fontWeight: 700, fontSize: 12, cursor: 'pointer',
          }}>
          {diagOpen ? '▾ Hide investigation' : '🔬 Investigate further (find duplicates / odd income)'}
        </button>

        {diagOpen && (
          <div style={{ marginTop: 12 }}>
            {diagLoading && !diagData && (
              <div style={{ fontSize: 12, color: '#94a3b8' }}>Running diagnostic…</div>
            )}
            {diagError && (
              <div style={{ fontSize: 12, color: '#dc2626' }}>{diagError}</div>
            )}
            {diagData && (
              <DiagnosticContent diag={diagData} onTransactionUpdated={onTransactionUpdated} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Renders the three diagnostic views inline. Pulled out to keep
// ZohoComparisonCard readable.
function DiagnosticContent({ diag, onTransactionUpdated }) {
  const fmtDateShort = iso => iso ? iso.slice(8,10) + '/' + iso.slice(5,7) + '/' + iso.slice(2,4) : '';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* 0) Duplicate-statement cleanup — Plan B. Read-only discovery
             + checkbox-driven bulk delete with backend safety re-check. */}
      <DuplicateStatementsBlock onAfterDelete={onTransactionUpdated} />

      {/* 1) Income by bank account */}
      <div>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#0F172A', marginBottom: 6, letterSpacing: 0.3 }}>
          INCOME BY BANK ACCOUNT
        </div>
        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: '#64748B', fontSize: 10, fontWeight: 700, textAlign: 'right' }}>
              <th style={{ textAlign: 'left', padding: '4px 8px' }}>ACCOUNT</th>
              <th style={{ padding: '4px 8px' }}>TXNS</th>
              <th style={{ padding: '4px 8px' }}>TOTAL (inc GST)</th>
              <th style={{ padding: '4px 8px' }}>TOTAL (ex GST)</th>
            </tr>
          </thead>
          <tbody>
            {(diag.income_by_bank || []).map(r => (
              <tr key={r.bank_code} style={{ borderTop: '1px solid #F1F5F9' }}>
                <td style={{ padding: '4px 8px', fontWeight: 700, color: '#0F172A' }}>{r.bank_name}</td>
                <td style={{ padding: '4px 8px', textAlign: 'right' }}>{r.txn_count}</td>
                <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600 }}>{fmtMoney0(r.total_credit_inc_gst)}</td>
                <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 600 }}>{fmtMoney0(r.total_credit_ex_gst)}</td>
              </tr>
            ))}
            {(diag.income_by_bank || []).length === 0 && (
              <tr><td colSpan={4} style={{ padding: '6px 8px', color: '#94a3b8', fontStyle: 'italic' }}>No income transactions in this period.</td></tr>
            )}
          </tbody>
        </table>
        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>
          ACTS = customer payments (legit). Sub-accounts shouldn't have Income — those are usually transfers.
        </div>
      </div>

      {/* 2) Potential duplicates — most actionable, shown next */}
      <div>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#0F172A', marginBottom: 6, letterSpacing: 0.3 }}>
          POTENTIAL DUPLICATES
          {diag.potential_duplicate_total > 0 && (
            <span style={{ marginLeft: 8, fontSize: 11, color: '#dc2626', fontWeight: 700 }}>
              · ~{fmtMoney0(diag.potential_duplicate_total)} of duplicate value
            </span>
          )}
        </div>
        {(diag.potential_duplicates || []).length === 0 ? (
          <div style={{ fontSize: 12, color: '#15803D', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 6, padding: 8 }}>
            ✓ No exact-duplicate Income transactions detected (same date + description + amount).
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {diag.potential_duplicates.map((g, i) => (
              <div key={i} style={{
                background: '#FEF2F2', border: '1px solid #FECACA',
                borderRadius: 6, padding: '6px 10px', fontSize: 11,
                display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 8, alignItems: 'center',
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: '#7F1D1D', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                       title={g.description || '(no description)'}>
                    {g.description || <em>(no description)</em>}
                  </div>
                  <div style={{ fontSize: 10, color: '#94a3b8' }}>
                    {fmtDateShort(g.txn_date)} · {(g.bank_codes || []).join(', ')} · IDs: {(g.ids || []).join(', ')}
                  </div>
                </div>
                <div style={{ fontWeight: 700, color: '#7F1D1D', whiteSpace: 'nowrap' }}>
                  {fmtMoney0(g.credit)} × {g.duplicate_count}
                </div>
                <div style={{ fontSize: 10, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                  excess ~{fmtMoney0(g.credit * (g.duplicate_count - 1))}
                </div>
              </div>
            ))}
            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>
              Same date + description + amount appearing 2+ times = likely a statement imported twice. Open Browse-All, find by ID, and delete the extra row.
            </div>
          </div>
        )}
      </div>

      {/* 3) Top 25 income transactions — for spotting weird ones */}
      <div>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#0F172A', marginBottom: 6, letterSpacing: 0.3 }}>
          TOP 25 INCOME CREDITS (any account)
        </div>
        <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: 6 }}>
          <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
            <thead style={{ position: 'sticky', top: 0, background: '#F8FAFC' }}>
              <tr style={{ color: '#64748B', fontSize: 10, fontWeight: 700 }}>
                <th style={{ textAlign: 'left',  padding: '4px 8px' }}>DATE</th>
                <th style={{ textAlign: 'left',  padding: '4px 8px' }}>BANK</th>
                <th style={{ textAlign: 'left',  padding: '4px 8px' }}>DESCRIPTION</th>
                <th style={{ textAlign: 'right', padding: '4px 8px' }}>AMOUNT</th>
                <th style={{ textAlign: 'left',  padding: '4px 8px' }}>ID</th>
              </tr>
            </thead>
            <tbody>
              {(diag.top_income_transactions || []).map(t => (
                <tr key={t.id} style={{ borderTop: '1px solid #F1F5F9' }}>
                  <td style={{ padding: '4px 8px', color: '#64748B' }}>{fmtDateShort(t.txn_date)}</td>
                  <td style={{ padding: '4px 8px', color: '#475569', fontWeight: 600 }}>{t.bank_account_code}</td>
                  <td style={{ padding: '4px 8px', color: '#0F172A', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={t.description || ''}>
                    {t.description || <em style={{ color: '#94a3b8' }}>—</em>}
                  </td>
                  <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 700, color: '#92400E' }}>{fmtMoney0(t.credit)}</td>
                  <td style={{ padding: '4px 8px', color: '#94a3b8', fontFamily: 'monospace' }}>#{t.id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>
          Scan for anything that doesn't look like a customer payment (Owner Loan, Tax Refund, Interest, internal transfers, etc).
        </div>
      </div>
    </div>
  );
}

// Plan B: read /find-duplicate-statements, let the boss check which
// statements to drop, then POST /delete-statements with confirm:'YES'.
// Server re-validates safety on the delete side, so a stale UI can't
// trick this into wiping a partial-duplicate statement by accident.
function DuplicateStatementsBlock({ onAfterDelete }) {
  const [loading, setLoading]   = React.useState(false);
  const [error,   setError]     = React.useState(null);
  const [data,    setData]      = React.useState(null);
  const [picked,  setPicked]    = React.useState(new Set());
  const [busy,    setBusy]      = React.useState(false);
  const [result,  setResult]    = React.useState(null); // { deleted, skipped } after POST

  async function fetchList() {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const r = await api.zohoFindDupStatements();
      setData(r);
      // Default-check every statement that's 100% redundant — those are
      // the obviously safe ones. Partial duplicates stay unchecked until
      // the boss explicitly opts in.
      const auto = new Set();
      for (const s of (r.statements || [])) if (s.safe_to_delete) auto.add(s.id);
      setPicked(auto);
    } catch (err) {
      setError(err.message || 'Failed to load duplicate statements');
    } finally {
      setLoading(false);
    }
  }

  function togglePick(id) {
    setPicked(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  }
  function pickAllSafe() {
    if (!data) return;
    setPicked(new Set(data.statements.filter(s => s.safe_to_delete).map(s => s.id)));
  }
  function pickAll() {
    if (!data) return;
    setPicked(new Set(data.statements.map(s => s.id)));
  }
  function pickNone() { setPicked(new Set()); }

  async function handleDelete() {
    if (picked.size === 0) return;
    const ids = [...picked];
    const partial = (data.statements || []).filter(s => picked.has(s.id) && !s.safe_to_delete);
    const warning = partial.length > 0
      ? `\n⚠ ${partial.length} of these are only PARTIALLY duplicate — the server will refuse those. Continue anyway?`
      : '';
    const sure = window.confirm(`Delete ${ids.length} statement${ids.length === 1 ? '' : 's'}?${warning}\n\nThis cascades to all their transactions. Cannot be undone.`);
    if (!sure) return;
    setBusy(true);
    try {
      const r = await api.zohoDeleteStatements(ids);
      setResult(r);
      // Refresh the discovery list so the deleted rows drop out, and
      // tell the parent to reload allTxns / balances / etc.
      await fetchList();
      if (onAfterDelete) onAfterDelete();
    } catch (err) {
      alert(`Delete failed: ${err.message || err}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{
      background: '#FEF2F2', border: '1px solid #FECACA',
      borderRadius: 10, padding: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#7F1D1D' }}>
            📋 Cleanup — duplicate statement detector
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
            Finds statements whose transactions are exact duplicates of earlier statements' rows.
          </div>
        </div>
        <button onClick={fetchList} disabled={loading}
          style={{
            padding: '6px 14px', borderRadius: 8, border: 'none',
            background: loading ? '#94a3b8' : '#0F172A', color: '#fff',
            fontWeight: 700, fontSize: 12, cursor: loading ? 'wait' : 'pointer',
          }}>
          {loading ? 'Scanning…' : (data ? '↻ Rescan' : '🔍 Scan for duplicate statements')}
        </button>
      </div>

      {error && (
        <div style={{ fontSize: 12, color: '#dc2626', marginTop: 8 }}>{error}</div>
      )}

      {result && (
        <div style={{
          marginTop: 10, padding: 8, borderRadius: 6,
          background: result.deleted.length ? '#F0FDF4' : '#FFFBEB',
          border: '1px solid ' + (result.deleted.length ? '#BBF7D0' : '#FCD34D'),
          color: result.deleted.length ? '#15803D' : '#92400E',
          fontSize: 12,
        }}>
          {result.deleted.length > 0 && <div>✓ Deleted {result.deleted.length} statement{result.deleted.length === 1 ? '' : 's'}: {result.deleted.join(', ')}</div>}
          {result.skipped.length > 0 && (
            <div style={{ marginTop: 4 }}>
              ⚠ Skipped {result.skipped.length}:{' '}
              {result.skipped.map(s => `#${s.id} (${s.reason})`).join(', ')}
            </div>
          )}
        </div>
      )}

      {data && data.statements.length === 0 && (
        <div style={{ marginTop: 10, fontSize: 12, color: '#15803D', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 6, padding: 8 }}>
          🎉 No duplicate statements found. The diff is coming from somewhere else.
        </div>
      )}

      {data && data.statements.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'baseline', marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#7F1D1D' }}>
              {data.statements.length} statement{data.statements.length === 1 ? '' : 's'} with duplicate content
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>
              total redundant value ≈ <strong>{fmtMoney0(data.total_redundant_value)}</strong>
              {' · '}
              <strong>{data.safe_statements_count}</strong> safe to bulk-delete (≈ {fmtMoney0(data.safe_redundant_value)})
            </div>
          </div>

          {/* Bulk-pick controls */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
            <button onClick={pickAllSafe} disabled={busy}
              style={btnSmall('#0F172A', '#fff')}>Select all safe ({data.safe_statements_count})</button>
            <button onClick={pickAll}     disabled={busy} style={btnSmall('#475569', '#fff')}>Select all ({data.statements.length})</button>
            <button onClick={pickNone}    disabled={busy} style={btnSmall('#fff', '#475569', '#CBD5E1')}>Clear</button>
          </div>

          <div style={{ maxHeight: 380, overflowY: 'auto', border: '1px solid #FECACA', borderRadius: 6 }}>
            <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
              <thead style={{ position: 'sticky', top: 0, background: '#FFE4E6' }}>
                <tr style={{ color: '#7F1D1D', fontWeight: 700 }}>
                  <th style={{ padding: '6px 4px', textAlign: 'center', width: 30 }}>PICK</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>STATEMENT</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>BANK</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>TXNS</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>REDUNDANT</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>EXCESS</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>SAFE?</th>
                </tr>
              </thead>
              <tbody>
                {data.statements.map(s => {
                  const checked = picked.has(s.id);
                  return (
                    <tr key={s.id}
                      style={{
                        borderTop: '1px solid #FECACA',
                        background: checked ? '#FEE2E2' : '#fff',
                        cursor: 'pointer',
                      }}
                      onClick={() => togglePick(s.id)}>
                      <td style={{ padding: '4px', textAlign: 'center' }}>
                        <input type="checkbox" checked={checked} onChange={() => togglePick(s.id)}
                          onClick={e => e.stopPropagation()} />
                      </td>
                      <td style={{ padding: '4px 8px' }}>
                        <div style={{ fontWeight: 700, color: '#0F172A' }}>
                          {s.period_label || `Statement #${s.id}`}
                        </div>
                        <div style={{ fontSize: 10, color: '#94a3b8' }}>#{s.id}</div>
                      </td>
                      <td style={{ padding: '4px 8px', color: '#475569' }}>{s.bank_account_code}</td>
                      <td style={{ padding: '4px 8px', textAlign: 'right' }}>{s.total_txn_count}</td>
                      <td style={{ padding: '4px 8px', textAlign: 'right', color: '#7F1D1D', fontWeight: 700 }}>
                        {s.redundant_txn_count}/{s.total_txn_count}
                      </td>
                      <td style={{ padding: '4px 8px', textAlign: 'right', color: '#7F1D1D', fontWeight: 700 }}>
                        {fmtMoney0(s.redundant_total_value)}
                      </td>
                      <td style={{ padding: '4px 8px' }}>
                        {s.safe_to_delete
                          ? <span style={{ background: '#DCFCE7', color: '#15803D', padding: '2px 6px', borderRadius: 999, fontWeight: 700 }}>✓ safe</span>
                          : <span style={{ background: '#FEF3C7', color: '#92400E', padding: '2px 6px', borderRadius: 999, fontWeight: 700 }}>⚠ partial</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Delete button */}
          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={handleDelete} disabled={busy || picked.size === 0}
              style={{
                padding: '8px 16px', borderRadius: 8, border: 'none',
                background: picked.size === 0 ? '#cbd5e1' : '#dc2626',
                color: '#fff', fontWeight: 800, fontSize: 13,
                cursor: busy || picked.size === 0 ? 'not-allowed' : 'pointer',
              }}>
              {busy ? '⏳ Deleting…' : `🗑 Delete ${picked.size} selected`}
            </button>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>
              Server re-checks each one before deleting. Partial-duplicates will be refused.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Reusable button style for the tiny "Select all / Clear" controls.
function btnSmall(bg, fg, border = '') {
  return {
    padding: '4px 10px', borderRadius: 6,
    border: border ? `1px solid ${border}` : 'none',
    background: bg, color: fg, fontWeight: 700, fontSize: 11, cursor: 'pointer',
  };
}

function CompareRow({ label, zoho, local, bold = false, note = null }) {
  const diff = (local || 0) - (zoho || 0);
  const diffSign = diff >= 0 ? '+' : '−';
  const diffColor = Math.abs(diff) < 1 ? '#16a34a' : (Math.abs(diff) > 5000 ? '#dc2626' : '#92400E');
  const warn = Math.abs(diff) > 5000;
  return (
    <tr style={{ borderTop: '1px solid #F1F5F9' }}>
      <td style={{ padding: '6px 8px', fontWeight: bold ? 700 : 500, color: '#475569' }}>
        {label}
        {note && <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 6 }}>({note})</span>}
      </td>
      <td style={{ padding: '6px 8px', textAlign: 'right', color: '#0F172A', fontWeight: bold ? 700 : 600 }}>
        {fmtMoney0(zoho || 0)}
      </td>
      <td style={{ padding: '6px 8px', textAlign: 'right', color: '#0F172A', fontWeight: bold ? 700 : 600 }}>
        {fmtMoney0(local || 0)}
      </td>
      <td style={{ padding: '6px 8px', textAlign: 'right', color: diffColor, fontWeight: 700 }}>
        {warn && '⚠ '}{diffSign}{fmtMoney0(Math.abs(diff))}
      </td>
    </tr>
  );
}

function BasStat({ label, value, valueColor = '#0F172A' }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: '#64748B', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: valueColor }}>{value}</div>
    </div>
  );
}

function DashboardShell({ children, period, setPeriod, currentFY, basPeriod, customStart, setCustomStart, customEnd, setCustomEnd }) {
  const btn = (key, label) => {
    const active = period === key;
    return (
      <button
        key={key}
        onClick={() => setPeriod(key)}
        style={{
          padding: '6px 14px', borderRadius: 999, border: '1px solid ' + (active ? '#1E3A4A' : '#E5E7EB'),
          background: active ? '#E8F2F7' : '#fff',
          color: active ? '#1E3A4A' : '#475569',
          fontSize: 12, fontWeight: 700, cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </button>
    );
  };
  return (
    <div style={{
      width: '100%', background: '#FAFAF7', border: '1px solid #E5E7EB',
      borderRadius: 16, padding: 18, marginBottom: 14,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <span style={{ fontSize: 13, color: '#475569', fontWeight: 600, marginRight: 4 }}>Period:</span>
        {btn('fy',     currentFY.label)}
        {btn('q',      `BAS · ${basPeriod.label}`)}
        {btn('month',  'This month')}
        {btn('lastfy', 'Last FY')}
        {btn('custom', 'Custom')}
        {period === 'custom' && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginLeft: 6 }}>
            <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
              style={{ fontSize: 12, padding: '5px 8px', border: '1px solid #E5E7EB', borderRadius: 6 }} />
            <span style={{ fontSize: 12, color: '#94a3b8' }}>→</span>
            <input type="date" value={customEnd}   onChange={e => setCustomEnd(e.target.value)}
              style={{ fontSize: 12, padding: '5px 8px', border: '1px solid #E5E7EB', borderRadius: 6 }} />
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

// ── Bank Accounts strip (4 cards + filter) ────────────────────────────────
// Per-account net + an "All accounts" pill for filtering. Includes a Rematch
// button that re-runs the auto-pairing across all transfers.
function BankAccountsStrip({ accounts, balances, filterAccountId, setFilterAccountId, onRematch, rematchBusy }) {
  // Render-safe fallback. If `/bank-accounts` failed but `/bank-accounts/balances`
  // returned the same shape (id, code, name, is_savings), use those — otherwise
  // we'd hide the whole strip on a transient API blip and the boss would lose
  // his account-filter UI for no good reason.
  let acctList = (accounts && accounts.length > 0) ? accounts : null;
  if (!acctList && balances && balances.length > 0) {
    acctList = balances.map(b => ({
      id: b.id, code: b.code, name: b.name, is_savings: b.is_savings,
    }));
  }
  if (!acctList || acctList.length === 0) return null;

  const ACCOUNT_TINTS = {
    ACTS:       { bg: '#E8F2F7', border: '#4A7A91', accent: '#1E3A4A' },
    ACTS_TX:    { bg: '#FEF3C7', border: '#F59E0B', accent: '#92400E' },
    GST:        { bg: '#DCFCE7', border: '#16A34A', accent: '#15803D' },
    SUPPORTING: { bg: '#FCE7F3', border: '#DB2777', accent: '#9D174D' },
    OLL:        { bg: '#F1F5F9', border: '#64748B', accent: '#334155' },
  };
  const balanceFor = (id) => (balances || []).find(b => b.id === id) || null;
  const totalUnmatched = (balances || []).reduce((s, b) => s + (b.unmatched_transfers || 0), 0);

  return (
    <div style={{ marginBottom: 14 }}>
      {/* Header: title + "All accounts" toggle + Rematch action.
          Per-account filter is now driven by clicking the cards themselves. */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A', letterSpacing: 0.2 }}>
          Bank Accounts
          {totalUnmatched > 0 && (
            <span style={{
              marginLeft: 10, background: '#FEE2E2', color: '#991B1B',
              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
            }}>
              {totalUnmatched} unmatched transfer{totalUnmatched === 1 ? '' : 's'}
            </span>
          )}
          <span style={{ marginLeft: 10, fontSize: 11, color: '#64748B', fontWeight: 500 }}>
            {filterAccountId === 'ALL'
              ? 'Showing all accounts — click a card to filter'
              : 'Click the highlighted card again to clear the filter'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            onClick={() => setFilterAccountId('ALL')}
            style={{
              padding: '5px 12px', fontSize: 11, fontWeight: 700, borderRadius: 999,
              border: filterAccountId === 'ALL' ? '2px solid #1E3A4A' : '1px solid #E5E7EB',
              background: filterAccountId === 'ALL' ? '#1E3A4A' : '#fff',
              color: filterAccountId === 'ALL' ? '#fff' : '#475569',
              cursor: 'pointer',
            }}
          >
            All accounts
          </button>
          <button
            onClick={onRematch}
            disabled={rematchBusy}
            style={{
              padding: '5px 12px', fontSize: 11, fontWeight: 700, borderRadius: 999,
              border: '1px solid #E5E7EB', background: '#fff', color: '#475569',
              cursor: rematchBusy ? 'wait' : 'pointer', opacity: rematchBusy ? 0.6 : 1,
            }}
            title="Re-run auto-pairing of Transfer Out / Transfer In rows"
          >
            {rematchBusy ? '⚙ Matching…' : '↻ Rematch transfers'}
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
        {acctList.map(a => {
          const b      = balanceFor(a.id);
          const tint   = ACCOUNT_TINTS[a.code] || { bg: '#F8FAFC', border: '#94A3B8', accent: '#475569' };
          const net    = b ? b.net : 0;
          const active = String(filterAccountId) === String(a.id);
          return (
            <div
              key={a.id}
              onClick={() => setFilterAccountId(active ? 'ALL' : String(a.id))}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setFilterAccountId(active ? 'ALL' : String(a.id)); }}
              style={{
                background: active ? '#fff' : tint.bg,
                border: active ? `2px solid ${tint.border}` : `1px solid ${tint.border}`,
                outline: active ? `4px solid ${tint.bg}` : 'none',
                borderRadius: 12,
                padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 4,
                cursor: 'pointer', userSelect: 'none',
                transition: 'transform 0.1s, box-shadow 0.15s',
                transform: active ? 'translateY(-1px)' : 'none',
                boxShadow: active ? `0 4px 12px ${tint.border}33` : 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: tint.accent, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                  {a.name}
                  {a.is_savings ? <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, opacity: 0.7 }}>SAVINGS</span> : null}
                </div>
                {active && <span style={{ fontSize: 10, fontWeight: 700, color: tint.border }}>● ACTIVE</span>}
              </div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#0F172A', letterSpacing: -0.3 }}>
                ${(net || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div style={{ fontSize: 11, color: '#64748B', display: 'flex', justifyContent: 'space-between' }}>
                <span>↑ ${Math.round(b?.total_in  || 0).toLocaleString('en-AU')}</span>
                <span>↓ ${Math.round(b?.total_out || 0).toLocaleString('en-AU')}</span>
                <span>{b?.txn_count || 0} txns</span>
              </div>
              {(b?.matched_transfers > 0 || b?.unmatched_transfers > 0) && (
                <div style={{ fontSize: 10, color: tint.accent, marginTop: 2 }}>
                  🔗 {b.matched_transfers} matched
                  {b.unmatched_transfers > 0 && <> · <span style={{ color: '#991B1B', fontWeight: 700 }}>{b.unmatched_transfers} unmatched</span></>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Split-transaction modal ────────────────────────────────────────────────
// Itemise one bank-line into multiple expense items each with its own
// category / sub-account / GST. Used to mirror the way Zoho shows e.g. a
// single $252.45 BPAY split into a GST-free vehicle license + a GST-bearing
// insurance line. Save validates that splits sum to the original amount.
function SplitModal({ txn, onClose, onSaved }) {
  const original = Math.abs(parseFloat(txn.debit) || 0) + Math.abs(parseFloat(txn.credit) || 0);
  const initial  = (Array.isArray(txn.splits) && txn.splits.length > 0)
    ? txn.splits.map(s => ({
        amount:   parseFloat(s.amount) || 0,
        category: s.category || 'Expense',
        account:  s.account  || 'Other Expenses',
        has_gst:  !!s.has_gst,
        notes:    s.notes || '',
      }))
    : [
        // Pre-fill with two halves so the boss isn't staring at an empty form
        { amount: +(original / 2).toFixed(2), category: 'Expense', account: 'Other Expenses', has_gst: true, notes: '' },
        { amount: +(original / 2).toFixed(2), category: 'Expense', account: 'Other Expenses', has_gst: true, notes: '' },
      ];

  const [splits, setSplits] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const sum     = splits.reduce((s, x) => s + (parseFloat(x.amount) || 0), 0);
  const diff    = +(original - sum).toFixed(2);
  const balanced = Math.abs(diff) < 0.01;

  function update(i, patch) {
    setSplits(prev => prev.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  }
  function addRow() {
    setSplits(prev => [...prev, { amount: Math.max(0, diff), category: 'Expense', account: 'Other Expenses', has_gst: true, notes: '' }]);
  }
  function removeRow(i) {
    setSplits(prev => prev.filter((_, idx) => idx !== i));
  }
  function autoBalance(i) {
    // Snap row i so the splits sum to the original
    update(i, { amount: +(parseFloat(splits[i].amount || 0) + diff).toFixed(2) });
  }

  async function handleSave() {
    setError('');
    if (!balanced) {
      setError(`Splits sum to $${sum.toFixed(2)} — needs to equal $${original.toFixed(2)} (off by $${diff.toFixed(2)}).`);
      return;
    }
    if (splits.length === 0) {
      setError('Add at least one split row, or use Clear to remove all splits.');
      return;
    }
    for (let i = 0; i < splits.length; i++) {
      const s = splits[i];
      if (!(parseFloat(s.amount) > 0))   { setError(`Row ${i + 1}: amount must be positive.`); return; }
      if (!s.category || !s.account)     { setError(`Row ${i + 1}: pick category and sub-account.`); return; }
    }
    setSaving(true);
    try {
      await api.setTransactionSplits(txn.id, splits.map(s => ({
        amount:   parseFloat(s.amount),
        category: s.category, account: s.account,
        has_gst:  !!s.has_gst, notes: s.notes || null,
      })));
      onSaved();
    } catch (e) {
      setError(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }
  async function handleClear() {
    if (!window.confirm('Remove all splits? This transaction goes back to a single category.')) return;
    setSaving(true);
    try { await api.setTransactionSplits(txn.id, []); onSaved(); }
    catch (e) { setError(e.message || 'Clear failed.'); }
    finally { setSaving(false); }
  }

  return (
    <div onClick={() => !saving && onClose()} style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 700,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 12, width: '100%', maxWidth: 760,
        maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#1E3A4A' }}>✂ Split transaction</div>
            <div style={{ fontSize: 12, color: '#64748B', marginTop: 3 }}>
              {txn.description} · ${original.toFixed(2)} · {(txn.txn_date || '').replace(/^(\d{4})-(\d{2})-(\d{2})$/, '$3/$2/$1')}
            </div>
          </div>
          <button onClick={onClose} disabled={saving} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: 20, padding: 4 }}>×</button>
        </div>

        <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
          {error && (
            <div style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', color: '#991B1B', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
              {error}
            </div>
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#F1F5F9' }}>
                <th style={{ ...thBrowse, width: 110 }}>Amount</th>
                <th style={thBrowse}>Category</th>
                <th style={thBrowse}>Sub-account</th>
                <th style={{ ...thBrowse, textAlign: 'center', width: 50 }}>GST</th>
                <th style={thBrowse}>Notes</th>
                <th style={{ width: 28 }}></th>
              </tr>
            </thead>
            <tbody>
              {splits.map((s, i) => {
                const subOpts = (ACCOUNTS[s.category]?.items || []).map(it => it.account);
                return (
                  <tr key={i} style={{ borderBottom: '1px solid #F1F5F9' }}>
                    <td style={{ padding: 4 }}>
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        <input type="number" step="0.01" min="0" value={s.amount}
                          onChange={e => update(i, { amount: e.target.value })}
                          style={{ width: 88, padding: '5px 8px', fontSize: 12, border: '1px solid #E5E7EB', borderRadius: 4, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }} />
                      </div>
                    </td>
                    <td style={{ padding: 4 }}>
                      <select value={s.category} onChange={e => {
                        const newCat = e.target.value;
                        const firstAcc = (ACCOUNTS[newCat]?.items || [])[0]?.account || '';
                        update(i, { category: newCat, account: firstAcc, has_gst: ACCOUNTS[newCat]?.items?.[0]?.hasGst ?? false });
                      }} style={selectBrowse}>
                        {Object.keys(ACCOUNTS).map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: 4 }}>
                      <select value={s.account} onChange={e => update(i, { account: e.target.value })} style={selectBrowse}>
                        {subOpts.map(a => <option key={a} value={a}>{a}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: 4, textAlign: 'center' }}>
                      <input type="checkbox" checked={!!s.has_gst} onChange={() => update(i, { has_gst: !s.has_gst })} />
                    </td>
                    <td style={{ padding: 4 }}>
                      <input type="text" value={s.notes} onChange={e => update(i, { notes: e.target.value })}
                        placeholder="optional" style={{ width: '100%', padding: '5px 8px', fontSize: 11, border: '1px solid #E5E7EB', borderRadius: 4 }} />
                    </td>
                    <td style={{ padding: 4, textAlign: 'center' }}>
                      <button onClick={() => removeRow(i)} disabled={splits.length <= 1}
                        style={{ background: 'none', border: 'none', color: splits.length <= 1 ? '#CBD5E1' : '#EF4444', cursor: splits.length <= 1 ? 'not-allowed' : 'pointer', fontSize: 14 }}>×</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
            <button onClick={addRow} style={{ padding: '6px 14px', fontSize: 12, fontWeight: 700, border: '1px dashed #CBD5E1', background: '#F8FAFC', borderRadius: 6, cursor: 'pointer', color: '#475569' }}>
              + Add split
            </button>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 12, color: '#64748B' }}>
              Sum: <strong style={{ color: balanced ? '#16A34A' : '#DC2626' }}>${sum.toFixed(2)}</strong>
              &nbsp;· Original: <strong>${original.toFixed(2)}</strong>
              {!balanced && (
                <>
                  &nbsp;· Diff: <strong style={{ color: '#DC2626' }}>${diff >= 0 ? '+' : ''}${diff.toFixed(2)}</strong>
                  <button onClick={() => autoBalance(splits.length - 1)} style={{ marginLeft: 8, padding: '3px 10px', fontSize: 11, fontWeight: 700, border: '1px solid #FCD34D', background: '#FEF3C7', color: '#92400E', borderRadius: 4, cursor: 'pointer' }}>
                    Auto-balance last row
                  </button>
                </>
              )}
            </span>
          </div>
        </div>

        <div style={{ padding: '14px 20px', borderTop: '1px solid #E5E7EB', display: 'flex', gap: 10, justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div>
            {txn.is_split && (
              <button onClick={handleClear} disabled={saving}
                style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #FCA5A5', background: '#FEF2F2', color: '#991B1B', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
                Remove all splits
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} disabled={saving}
              style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #E5E7EB', background: '#fff', color: '#475569', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving || !balanced}
              style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: balanced ? '#1E3A4A' : '#94A3B8', color: '#fff', fontWeight: 800, fontSize: 13, cursor: balanced ? 'pointer' : 'not-allowed', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving…' : `Save ${splits.length} split${splits.length === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Browse-all-transactions view ───────────────────────────────────────────
// Cross-statement transaction browser with category / sub-category / account /
// date / search filters. Built for the boss to verify categorisation across
// the full ~2,000-row history without having to open each weekly statement.
function BrowseAllTransactions({
  allTxns, bankAccounts, bankBalances, filterAccountId, setFilterAccountId,
  browseCat, setBrowseCat,
  browseAccount, setBrowseAccount,
  browseSearch, setBrowseSearch,
  browseFrom, setBrowseFrom,
  browseTo, setBrowseTo,
  browseSavingId,
  onUpdate, onSplit,
}) {
  // Sub-category options scoped to the picked main category.
  const subAccounts = browseCat === 'ALL'
    ? []
    : (ACCOUNTS[browseCat]?.items || []).map(i => i.account);

  // Apply all filters in one pass.
  const filtered = useMemo(() => {
    const q = browseSearch.trim().toLowerCase();
    return (allTxns || []).filter(t => {
      if (filterAccountId !== 'ALL' && t.bank_account_id !== parseInt(filterAccountId)) return false;
      if (browseCat !== 'ALL' && (t.category || 'Expense') !== browseCat) return false;
      if (browseAccount !== 'ALL' && (t.account || '') !== browseAccount) return false;
      if (browseFrom && (t.txn_date || '') < browseFrom) return false;
      if (browseTo   && (t.txn_date || '') > browseTo)   return false;
      if (q) {
        const hay = ((t.description || '') + ' ' + (t.bank_account_name || '') + ' ' + (t.account || '')).toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [allTxns, filterAccountId, browseCat, browseAccount, browseSearch, browseFrom, browseTo]);

  // Aggregate counts/sums for the filter chips so the boss sees impact.
  const stats = useMemo(() => {
    const s = { count: filtered.length, debit: 0, credit: 0, net: 0 };
    for (const t of filtered) {
      s.debit  += parseFloat(t.debit)  || 0;
      s.credit += parseFloat(t.credit) || 0;
    }
    s.net = s.credit - s.debit;
    return s;
  }, [filtered]);

  // Per-category counts (for the chip badges) — counts BEFORE category filter
  // so all chips stay visible even after one is selected.
  const catCounts = useMemo(() => {
    const base = (allTxns || []).filter(t => {
      if (filterAccountId !== 'ALL' && t.bank_account_id !== parseInt(filterAccountId)) return false;
      if (browseFrom && (t.txn_date || '') < browseFrom) return false;
      if (browseTo   && (t.txn_date || '') > browseTo)   return false;
      const q = browseSearch.trim().toLowerCase();
      if (q) {
        const hay = ((t.description || '') + ' ' + (t.bank_account_name || '') + ' ' + (t.account || '')).toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const c = { ALL: base.length, Income: 0, Expense: 0, Transfer: 0 };
    for (const t of base) {
      const cat = t.category || 'Expense';
      if (c[cat] != null) c[cat]++;
    }
    return c;
  }, [allTxns, filterAccountId, browseFrom, browseTo, browseSearch]);

  // Per-sub-category counts (within selected main category)
  const accCounts = useMemo(() => {
    if (browseCat === 'ALL') return {};
    const base = (allTxns || []).filter(t => {
      if (filterAccountId !== 'ALL' && t.bank_account_id !== parseInt(filterAccountId)) return false;
      if ((t.category || 'Expense') !== browseCat) return false;
      if (browseFrom && (t.txn_date || '') < browseFrom) return false;
      if (browseTo   && (t.txn_date || '') > browseTo)   return false;
      const q = browseSearch.trim().toLowerCase();
      if (q) {
        const hay = ((t.description || '') + ' ' + (t.bank_account_name || '') + ' ' + (t.account || '')).toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const c = { ALL: base.length };
    for (const t of base) {
      const a = t.account || '';
      if (a) c[a] = (c[a] || 0) + 1;
    }
    return c;
  }, [allTxns, filterAccountId, browseCat, browseFrom, browseTo, browseSearch]);

  const ACCOUNT_TINTS = {
    ACTS:       '#1E3A4A',
    ACTS_TX:    '#92400E',
    GST:        '#15803D',
    SUPPORTING: '#9D174D',
    OLL:        '#334155',
  };
  function badgeForBank(t) {
    const code = t.bank_account_code || '';
    return (
      <span style={{
        background: '#fff',
        color: ACCOUNT_TINTS[code] || '#475569',
        border: `1px solid ${ACCOUNT_TINTS[code] || '#CBD5E1'}`,
        padding: '1px 6px', borderRadius: 999, fontSize: 9, fontWeight: 700, whiteSpace: 'nowrap',
      }}>{code || '—'}</span>
    );
  }

  return (
    <div style={{ background: '#fff', border: '1px solid #E8E8E8', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
      {/* ── Header strip ── */}
      <div style={{ background: NAVY, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ color: '#fff', fontWeight: 800, fontSize: 14, flex: 1 }}>
          🔍 Browse all transactions
        </div>
        <div style={{ fontSize: 11, color: '#94a3b8' }}>
          {stats.count} of {(allTxns || []).length} txns · ↑ ${stats.credit.toFixed(2)} · ↓ ${stats.debit.toFixed(2)} · net ${stats.net >= 0 ? '+' : ''}${stats.net.toFixed(2)}
        </div>
      </div>

      {/* ── Category chips ── */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '10px 14px', borderBottom: '1px solid #E5E7EB', background: '#F8FAFC', alignItems: 'center' }}>
        {['ALL', 'Income', 'Expense', 'Transfer'].map(cat => {
          const isActive = browseCat === cat;
          const color = cat === 'ALL' ? NAVY : (ACCOUNTS[cat]?.color || '#6b7280');
          const count = catCounts[cat] || 0;
          return (
            <button key={cat} onClick={() => { setBrowseCat(cat); setBrowseAccount('ALL'); }}
              style={{
                padding: '4px 12px', fontSize: 11, fontWeight: 700, borderRadius: 999, cursor: 'pointer',
                border: isActive ? `2px solid ${color}` : '1px solid #E5E7EB',
                background: isActive ? color : '#fff',
                color: isActive ? '#fff' : '#475569',
                whiteSpace: 'nowrap',
              }}>
              {cat === 'ALL' ? 'All' : cat} <span style={{ opacity: 0.8 }}>({count})</span>
            </button>
          );
        })}
        <span style={{ flex: 1 }} />

        {/* Account picker — drives the top-level filterAccountId so the rest
            of the page (KPI cards, BAS) also responds. Saves a scroll up to
            the bank-accounts strip when you just want one account here. */}
        {setFilterAccountId && (() => {
          const accountList = (bankAccounts && bankAccounts.length > 0)
            ? bankAccounts
            : (bankBalances || []);
          if (accountList.length === 0) return null;
          return (
            <select
              value={String(filterAccountId)}
              onChange={e => setFilterAccountId(e.target.value)}
              title="Filter by bank account"
              style={{
                padding: '5px 10px', fontSize: 11, fontWeight: 700,
                border: '1px solid #E5E7EB', borderRadius: 6,
                background: filterAccountId === 'ALL' ? '#fff' : '#E8F2F7',
                color: filterAccountId === 'ALL' ? '#475569' : '#1E3A4A',
                cursor: 'pointer',
              }}
            >
              <option value="ALL">📂 All accounts</option>
              {accountList.map(a => (
                <option key={a.id} value={a.id}>📂 {a.name}</option>
              ))}
            </select>
          );
        })()}

        {/* Search + date range live on the same row */}
        <input
          value={browseSearch}
          onChange={e => setBrowseSearch(e.target.value)}
          placeholder="Search description…"
          style={{ width: 180, padding: '5px 10px', border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 12 }}
        />
        <input type="date" value={browseFrom} onChange={e => setBrowseFrom(e.target.value)}
          style={{ padding: '5px 8px', border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 11 }} />
        <span style={{ fontSize: 11, color: '#94a3b8' }}>→</span>
        <input type="date" value={browseTo} onChange={e => setBrowseTo(e.target.value)}
          style={{ padding: '5px 8px', border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 11 }} />
        {(browseCat !== 'ALL' || browseAccount !== 'ALL' || browseSearch || browseFrom || browseTo || filterAccountId !== 'ALL') && (
          <button
            onClick={() => {
              setBrowseCat('ALL'); setBrowseAccount('ALL'); setBrowseSearch('');
              setBrowseFrom(''); setBrowseTo('');
              if (setFilterAccountId) setFilterAccountId('ALL');
            }}
            style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 6, border: '1px solid #FCA5A5', background: '#FEF2F2', color: '#991B1B', cursor: 'pointer' }}
          >× Clear</button>
        )}
      </div>

      {/* ── Sub-category chips (only when a main category is selected) ── */}
      {browseCat !== 'ALL' && subAccounts.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, padding: '7px 14px', borderBottom: '1px solid #E5E7EB', background: '#F1F5F9', alignItems: 'center' }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', marginRight: 4 }}>Sub:</span>
          <button onClick={() => setBrowseAccount('ALL')} style={{
            padding: '3px 10px', fontSize: 10.5, fontWeight: 700, borderRadius: 999, cursor: 'pointer',
            border: browseAccount === 'ALL' ? `2px solid ${ACCOUNTS[browseCat]?.color || '#6b7280'}` : '1px solid #E5E7EB',
            background: browseAccount === 'ALL' ? (ACCOUNTS[browseCat]?.color || '#6b7280') : '#fff',
            color: browseAccount === 'ALL' ? '#fff' : '#6b7280',
          }}>
            All <span style={{ opacity: 0.8 }}>({accCounts.ALL || 0})</span>
          </button>
          {subAccounts.map(acc => {
            const isActive = browseAccount === acc;
            const accentColor = ACCOUNT_ROW_COLORS[acc]?.border || ACCOUNTS[browseCat]?.color || '#6b7280';
            const cnt = accCounts[acc] || 0;
            if (cnt === 0) return null;
            return (
              <button key={acc} onClick={() => setBrowseAccount(acc)} style={{
                padding: '3px 10px', fontSize: 10.5, fontWeight: 700, borderRadius: 999, cursor: 'pointer',
                border: isActive ? `2px solid ${accentColor}` : '1px solid #E5E7EB',
                background: isActive ? accentColor : '#fff',
                color: isActive ? '#fff' : '#6b7280', whiteSpace: 'nowrap',
              }}>
                {acc} <span style={{ opacity: 0.8 }}>({cnt})</span>
              </button>
            );
          })}
        </div>
      )}

      {/* ── Table ── */}
      {/* Generous max-height so the table fills the screen alongside the
          taller Saved Statements sidebar (300+ entries) instead of ending
          short with empty space below it. */}
      <div style={{ maxHeight: 'calc(100vh - 220px)', overflow: 'auto' }}>
        {filtered.length === 0 ? (
          <div style={{ padding: '40px 16px', textAlign: 'center', color: '#94A3B8', fontSize: 13 }}>
            No transactions match this filter.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead style={{ position: 'sticky', top: 0, background: '#F1F5F9', zIndex: 1 }}>
              <tr>
                <th style={thBrowse}>Date</th>
                <th style={thBrowse}>Acct</th>
                <th style={thBrowse}>Description</th>
                <th style={{ ...thBrowse, textAlign: 'right' }}>Debit</th>
                <th style={{ ...thBrowse, textAlign: 'right' }}>Credit</th>
                <th style={thBrowse}>Category</th>
                <th style={thBrowse}>Sub-account</th>
                <th style={{ ...thBrowse, textAlign: 'center' }}>GST</th>
                <th style={{ ...thBrowse, textAlign: 'center' }}>Split</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 500).map((t, i) => {
                const cat = t.category || 'Expense';
                const subOpts = (ACCOUNTS[cat]?.items || []).map(it => it.account);
                const isSaving = browseSavingId === t.id;
                return (
                  <tr key={t.id} style={{
                    borderBottom: '1px solid #F1F5F9',
                    // Synthetic auto-link rows get a soft yellow tint so
                    // they're visually distinct from imported bank lines.
                    background: t.is_synthetic ? '#FFFBEB' : (i % 2 ? '#FAFAF7' : '#fff'),
                  }}>
                    <td style={tdBrowse}>{(t.txn_date || '').replace(/^(\d{4})-(\d{2})-(\d{2})$/, '$3/$2/$1')}</td>
                    <td style={tdBrowse}>
                      {badgeForBank(t)}
                      {t.is_synthetic ? (
                        <span title="Auto-linked from the other account — no real bank entry imported"
                          style={{ marginLeft: 4, fontSize: 9, fontWeight: 700, color: '#92400E', background: '#FEF3C7', border: '1px solid #FCD34D', padding: '1px 5px', borderRadius: 999 }}>
                          🔗 AUTO
                        </span>
                      ) : null}
                    </td>
                    <td style={{ ...tdBrowse, maxWidth: 360, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                      {t.description}
                      {t.transfer_pair_id && (() => {
                        // Find the partner row in the loaded list (same pair_id, different id)
                        const partner = (allTxns || []).find(
                          x => x.transfer_pair_id === t.transfer_pair_id && x.id !== t.id
                        );
                        if (!partner) return null;
                        const pCode = partner.bank_account_code || '?';
                        return (
                          <span
                            title={`Paired with ${partner.bank_account_name || pCode} on ${partner.txn_date} — categorising this row will also update the pair`}
                            style={{
                              display: 'inline-block', marginLeft: 6,
                              padding: '1px 7px', borderRadius: 999,
                              background: '#EFF6FF', color: '#1E3A8A',
                              border: '1px solid #BFDBFE',
                              fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap',
                            }}
                          >
                            🔗 ↔ {pCode}
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ ...tdBrowse, textAlign: 'right', color: t.debit > 0 ? '#dc2626' : '#cbd5e1', fontWeight: t.debit > 0 ? 700 : 400 }}>
                      {t.debit > 0 ? `$${(parseFloat(t.debit) || 0).toFixed(2)}` : '—'}
                    </td>
                    <td style={{ ...tdBrowse, textAlign: 'right', color: t.credit > 0 ? '#16a34a' : '#cbd5e1', fontWeight: t.credit > 0 ? 700 : 400 }}>
                      {t.credit > 0 ? `$${(parseFloat(t.credit) || 0).toFixed(2)}` : '—'}
                    </td>
                    <td style={tdBrowse}>
                      {t.is_split ? (
                        <span style={{ fontSize: 10, fontWeight: 700, color: '#7C3AED', background: '#F5F3FF', border: '1px solid #DDD6FE', padding: '2px 8px', borderRadius: 999 }}>
                          📌 SPLIT ({(t.splits || []).length})
                        </span>
                      ) : (
                        <select
                          value={cat}
                          disabled={isSaving}
                          onChange={e => {
                            const newCat = e.target.value;
                            const firstAcc = (ACCOUNTS[newCat]?.items || [])[0]?.account || '';
                            onUpdate(t, {
                              category: newCat, account: firstAcc,
                              has_gst: ACCOUNTS[newCat]?.items?.[0]?.hasGst ?? false,
                            });
                          }}
                          style={selectBrowse}
                        >
                          {Object.keys(ACCOUNTS).map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      )}
                    </td>
                    <td style={tdBrowse}>
                      {t.is_split ? (
                        <span style={{ fontSize: 11, color: '#7C3AED', fontStyle: 'italic' }}>
                          {(t.splits || []).map(s => s.account).join(' + ')}
                        </span>
                      ) : (
                        <select
                          value={t.account || ''}
                          disabled={isSaving}
                          onChange={e => onUpdate(t, { account: e.target.value })}
                          style={selectBrowse}
                        >
                          {subOpts.map(a => <option key={a} value={a}>{a}</option>)}
                        </select>
                      )}
                    </td>
                    <td style={{ ...tdBrowse, textAlign: 'center' }}>
                      {t.is_split ? (
                        <span style={{ fontSize: 10, color: '#94A3B8' }}>—</span>
                      ) : (
                        <input
                          type="checkbox"
                          checked={!!t.has_gst}
                          disabled={isSaving}
                          onChange={() => onUpdate(t, { has_gst: !t.has_gst })}
                        />
                      )}
                    </td>
                    <td style={{ ...tdBrowse, textAlign: 'center' }}>
                      <button
                        onClick={() => onSplit(t)}
                        title={t.is_split ? 'Edit splits' : 'Split this transaction into multiple categories'}
                        style={{
                          padding: '3px 8px', fontSize: 10, fontWeight: 700,
                          border: `1px solid ${t.is_split ? '#7C3AED' : '#CBD5E1'}`,
                          background: t.is_split ? '#F5F3FF' : '#fff',
                          color: t.is_split ? '#7C3AED' : '#64748B',
                          borderRadius: 4, cursor: 'pointer',
                        }}
                      >✂ {t.is_split ? 'Edit' : 'Split'}</button>
                    </td>
                  </tr>
                );
              })}
              {filtered.length > 500 && (
                <tr>
                  <td colSpan={9} style={{ padding: 12, textAlign: 'center', fontSize: 11, color: '#64748B', background: '#FEF3C7' }}>
                    Showing first 500 of {filtered.length} matches. Narrow the filters to see more.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const thBrowse = { padding: '8px 10px', fontSize: 11, fontWeight: 700, color: '#475569', textAlign: 'left', whiteSpace: 'nowrap', borderBottom: '2px solid #E5E7EB' };
const tdBrowse = { padding: '6px 10px', verticalAlign: 'middle', whiteSpace: 'nowrap' };
const selectBrowse = {
  padding: '4px 6px', fontSize: 11, border: '1px solid #E5E7EB', borderRadius: 4,
  background: '#fff', color: '#0F172A', cursor: 'pointer', minWidth: 100,
};

// ── Main component ─────────────────────────────────────────────────────────
export default function StatementPanel() {
  const [statements,   setStatements]  = useState([]);
  const [selectedId,   setSelectedId]  = useState(null);
  const [listLoading,  setListLoading] = useState(false);
  const [allTxns,      setAllTxns]     = useState([]); // ALL transactions across all statements

  // Boss's 4 fixed bank accounts (loaded once); current pick for new statement
  const [bankAccounts,    setBankAccounts]    = useState([]);
  const [bankBalances,    setBankBalances]    = useState([]);
  const [pickedAccountId, setPickedAccountId] = useState('');
  const [filterAccountId, setFilterAccountId] = useState('ALL'); // 'ALL' | id
  const [rematchBusy,     setRematchBusy]     = useState(false);

  const [periodLabel, setPeriodLabel] = useState('');
  const [weekStart,   setWeekStart]   = useState('');
  const [weekEnd,     setWeekEnd]     = useState('');
  const [rawText,     setRawText]     = useState('');
  const [parsed,      setParsed]      = useState(null);
  const [saving,      setSaving]      = useState(false);
  const [parseError,  setParseError]  = useState('');

  const [statement,    setStatement]   = useState(null);
  const [transactions, setTransactions]= useState([]);

  const [filterCat,      setFilterCat]      = useState('ALL');
  const [filterAccount,  setFilterAccount]  = useState('ALL');
  const [collapsedMonths, setCollapsedMonths] = useState({});
  const [collapsedWeeks,  setCollapsedWeeks]  = useState({});
  const [collapsedYears,  setCollapsedYears]  = useState({});

  // New-statement modal + saved-statements search.
  // Form lives in a modal now to free up the sidebar for browsing 300+ entries.
  const [showNewModal,   setShowNewModal]   = useState(false);
  const [stmtSearch,     setStmtSearch]     = useState('');

  // Track whether the user has manually edited the period label so the
  // auto-suggest doesn't clobber a custom value. Reset to "auto" mode
  // every time the modal opens fresh or after a save clears the form.
  const [labelEdited,    setLabelEdited]    = useState(false);

  // Auto-fill the period label whenever account / dates change AND the
  // user hasn't typed a custom name. Mirrors the import script's format
  // ("ACTS · Week 19 / 2026 (04/05–10/05)") so new entries blend with
  // the bulk-imported ones in the saved-statements list.
  useEffect(() => {
    if (labelEdited) return;
    if (!showNewModal) return;
    if (!pickedAccountId || !weekStart) return;
    const list = bankAccounts.length > 0 ? bankAccounts : bankBalances;
    const acct = list.find(a => String(a.id) === String(pickedAccountId));
    if (!acct?.code) return;
    const w = getISOWeekInfo(weekStart);
    const dms = weekStart.slice(8, 10) + '/' + weekStart.slice(5, 7);
    const endIso = weekEnd || weekStart;
    const dme = endIso.slice(8, 10) + '/' + endIso.slice(5, 7);
    setPeriodLabel(`${acct.code} · Week ${w.week} / ${w.year} (${dms}–${dme})`);
  }, [pickedAccountId, weekStart, weekEnd, showNewModal, labelEdited, bankAccounts, bankBalances]);

  // Browse-all-transactions mode + its filters
  const [viewMode,         setViewMode]         = useState('statement'); // 'statement' | 'browse'
  const [browseCat,        setBrowseCat]        = useState('ALL');
  const [browseAccount,    setBrowseAccount]    = useState('ALL');
  const [browseSearch,     setBrowseSearch]     = useState('');
  const [browseFrom,       setBrowseFrom]       = useState('');
  const [browseTo,         setBrowseTo]         = useState('');
  const [browseSavingId,   setBrowseSavingId]   = useState(null);

  // Split modal — opened from the ✂ button on either view
  const [splitTxn, setSplitTxn] = useState(null);

  function toggleMonth(key) { setCollapsedMonths(p => ({...p, [key]: !p[key]})); }
  function toggleWeek(key)  { setCollapsedWeeks(p =>  ({...p, [key]: !p[key]})); }
  function toggleYear(yr)   { setCollapsedYears(p =>  ({...p, [yr]:  !p[yr] })); }

  // ── Load list + all transactions ──────────────────────────────────────────
  const loadList = useCallback(async () => {
    setListLoading(true);
    // Promise.allSettled — one failing endpoint must NOT clear the others.
    // (Was Promise.all: a single 500 on /bank-accounts/balances was nuking
    //  the saved-statements list too, which is why the page rendered empty
    //  right after the bulk import.)
    const [stmtsRes, txnsRes, acctsRes, balancesRes] = await Promise.allSettled([
      api.getStatements(),
      api.getAllTransactions(),
      api.getBankAccounts(),
      api.getBankAccountBalances(),
    ]);
    if (stmtsRes.status   === 'fulfilled') setStatements(stmtsRes.value);
    else console.error('getStatements failed:', stmtsRes.reason);
    if (txnsRes.status    === 'fulfilled') setAllTxns(txnsRes.value);
    else console.error('getAllTransactions failed:', txnsRes.reason);
    if (acctsRes.status   === 'fulfilled') setBankAccounts(acctsRes.value);
    else console.error('getBankAccounts failed:', acctsRes.reason);
    if (balancesRes.status === 'fulfilled') setBankBalances(balancesRes.value);
    else console.error('getBankAccountBalances failed:', balancesRes.reason);
    // Default the New Statement form to ACTS Account on first load.
    // Fall back to the balances response when /bank-accounts is unavailable
    // (same shape: id, code, name) so the dropdown still gets pre-selected.
    if (!pickedAccountId) {
      const source = (acctsRes.status === 'fulfilled' && acctsRes.value.length > 0)
        ? acctsRes.value
        : (balancesRes.status === 'fulfilled' ? balancesRes.value : []);
      if (source.length > 0) {
        const acts = source.find(a => a.code === 'ACTS') || source[0];
        setPickedAccountId(String(acts.id));
      }
    }
    setListLoading(false);
  }, [pickedAccountId]);
  useEffect(() => { loadList(); }, [loadList]);

  async function loadStatement(id) {
    setSelectedId(id); setParsed(null); setFilterCat('ALL'); setFilterAccount('ALL');
    try {
      const data = await api.getStatement(id);
      setStatement(data); setTransactions(data.transactions || []);
    } catch(e) { console.error(e); }
  }

  function handleParse() {
    setParseError('');
    if (!rawText.trim()) { setParseError('Paste bank statement text first.'); return; }
    const result = parseBankText(rawText);
    if (!result.length) { setParseError('No transactions found. Expected format: Date ‹tab› Amount ‹tab› Description ‹tab› Balance'); return; }
    setParsed(result); setStatement(null); setSelectedId(null); setFilterCat('ALL'); setFilterAccount('ALL');
  }

  async function handleSave() {
    if (!periodLabel.trim()) { alert('Enter a period label (e.g. "Apr 2026")'); return; }
    if (!parsed?.length) { alert('Parse the statement first.'); return; }
    if (!pickedAccountId)  { alert('Pick which bank account this statement belongs to.'); return; }
    setSaving(true);
    try {
      const result = await api.createStatement({
        period_label: periodLabel.trim(),
        week_start: weekStart||null, week_end: weekEnd||null,
        raw_text: rawText, transactions: parsed,
        bank_account_id: parseInt(pickedAccountId),
      });
      await loadList(); // also refreshes allTxns + balances + matches
      await loadStatement(result.id);
      setPeriodLabel(''); setWeekStart(''); setWeekEnd(''); setRawText(''); setParsed(null);
      setLabelEdited(false); // back to auto mode for the next statement
    } catch(e) { alert(e.message); } finally { setSaving(false); }
  }

  async function handleRematch() {
    if (!window.confirm('Rerun transfer matching across all accounts? Existing pairs will be recomputed from scratch.')) return;
    setRematchBusy(true);
    try {
      const r = await api.rematchTransfers();
      await loadList();
      alert(`✓ ${r.matched} matched · ${r.unmatched} still unmatched`);
    } catch (e) { alert(e.message); }
    finally     { setRematchBusy(false); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this statement and all its transactions?')) return;
    try {
      await api.deleteStatement(id);
      setStatements(prev => prev.filter(s => s.id !== id));
      if (selectedId === id) { setSelectedId(null); setStatement(null); setTransactions([]); }
    } catch(e) { alert(e.message); }
  }

  // ── Unified transaction updater ──────────────────────────────────────────
  // Both views (per-statement viewer + Browse-All) hit this. It keeps three
  // pieces of state in lock-step so the boss never sees stale data after an
  // edit:
  //   1. `transactions` — currently-loaded statement viewer
  //   2. `allTxns`     — Browse-All source
  //   3. the pair row, if this txn has a transfer_pair_id (matches the
  //                     pair_propagation we just added on the backend)
  //
  // `txn` must include id, statement_id, and (optionally) transfer_pair_id.
  async function applyTxnUpdate(txn, patch) {
    const stmtId = txn.statement_id ?? selectedId;
    const merged = { ...txn, ...patch };

    const matchesId   = (t) => t.id === txn.id;
    const matchesPair = (t) =>
      txn.transfer_pair_id && t.id !== txn.id && t.transfer_pair_id === txn.transfer_pair_id;

    const partnerPatch = (t) => ({
      // Only sync the categorisation fields — keep description, amounts etc.
      category: merged.category, account: merged.account,
      has_gst:  merged.has_gst,  notes:   merged.notes ?? t.notes,
    });

    // Optimistic update across both arrays
    setTransactions(prev => prev.map(t =>
      matchesId(t)   ? merged :
      matchesPair(t) ? { ...t, ...partnerPatch(t) } : t
    ));
    setAllTxns(prev => prev.map(t =>
      matchesId(t)   ? merged :
      matchesPair(t) ? { ...t, ...partnerPatch(t) } : t
    ));

    try {
      await api.updateTransaction(stmtId, txn.id, {
        category: merged.category, account: merged.account,
        has_gst:  merged.has_gst,  notes:   merged.notes ?? '',
      });
    } catch (err) {
      // Roll back on failure
      setTransactions(prev => prev.map(t => t.id === txn.id ? txn : (matchesPair(t) ? t : t)));
      setAllTxns    (prev => prev.map(t => t.id === txn.id ? txn : (matchesPair(t) ? t : t)));
      console.error('updateTransaction failed', err);
      alert(err.message || 'Update failed');
    }
  }

  // Backwards-compat alias used by the per-statement viewer's existing code
  async function saveUpdate(txn, patch) {
    return applyTxnUpdate(txn, patch);
  }

  // Update a parsed (preview) transaction
  function updateParsed(idx, patch) {
    setParsed(prev => prev.map((t,i) => i === idx ? {...t, ...patch} : t));
  }

  // When account changes, auto-set category + GST
  function handleAccountChange(t, idx, newAccount, isParsed) {
    const newCat = accountCategory(newAccount);
    const newGst = defaultGst(newAccount);
    if (isParsed) updateParsed(idx, { account: newAccount, category: newCat, has_gst: newGst });
    else saveUpdate(t, { account: newAccount, category: newCat, has_gst: newGst });
  }

  function handleGstToggle(t, idx, isParsed) {
    if (isParsed) updateParsed(idx, { has_gst: !t.has_gst });
    else saveUpdate(t, { has_gst: !t.has_gst });
  }

  // ── Derived data ───────────────────────────────────────────────────────────
  const displayTxns  = parsed || transactions;
  const filteredTxns = useMemo(() => {
    let txns = filterCat === 'ALL' ? displayTxns : displayTxns.filter(t => (t.category||'Expense') === filterCat);
    if (filterAccount !== 'ALL') txns = txns.filter(t => (t.account||'') === filterAccount);
    return txns;
  }, [displayTxns, filterCat, filterAccount]);
  const monthGroups  = useMemo(() => groupByMonthAndWeek(filteredTxns), [filteredTxns]);
  const stmtByYear   = useMemo(() => groupStatementsByYear(statements), [statements]);
  const bas          = useMemo(() => computeBAS(displayTxns), [displayTxns]);

  // ── Available months from all transactions ────────────────────────────────
  const availableMonths = useMemo(() => {
    const seen = {};
    for (const t of allTxns) {
      if (!t.txn_date) continue;
      const [y, m] = t.txn_date.split('-');
      const key = `${y}-${m}`;
      if (!seen[key]) seen[key] = { key, year: parseInt(y), month: parseInt(m), label: `${MONTH_NAMES[parseInt(m)-1]} ${y}` };
    }
    return Object.values(seen).sort((a, b) => b.key.localeCompare(a.key));
  }, [allTxns]);

  const [summaryTab,    setSummaryTab]    = useState('monthly');
  const [selectedMonth, setSelectedMonth] = useState('');

  // Default to most recent month when data loads
  useEffect(() => {
    if (availableMonths.length > 0 && !selectedMonth) {
      setSelectedMonth(availableMonths[0].key);
    }
  }, [availableMonths, selectedMonth]);

  // ── BAS for selected month ────────────────────────────────────────────────
  const selectedMonthBAS = useMemo(() => {
    if (!selectedMonth) return null;
    const txns = allTxns.filter(t => t.txn_date && t.txn_date.startsWith(selectedMonth));
    const mo = availableMonths.find(m => m.key === selectedMonth);
    return { label: mo?.label || selectedMonth, bas: computeBAS(txns) };
  }, [allTxns, selectedMonth, availableMonths]);

  // ── BAS per week within selected month ───────────────────────────────────
  const selectedWeeksBAS = useMemo(() => {
    if (!selectedMonth) return [];
    // Get all txns for the month + neighbouring days (weeks can span month boundaries)
    const [sy, sm] = selectedMonth.split('-').map(Number);
    const weekMap = {};
    for (const t of allTxns) {
      if (!t.txn_date) continue;
      const info = getISOWeekInfo(t.txn_date);
      const key  = `${info.year}-W${String(info.week).padStart(2,'0')}`;
      // Include week if its Monday falls in selected month OR the txn itself is in selected month
      const txnMonth = t.txn_date.slice(0, 7);
      const monMonth = info.weekStart ? info.weekStart.slice(0, 7) : '';
      if (txnMonth !== selectedMonth && monMonth !== selectedMonth) continue;
      if (!weekMap[key]) weekMap[key] = { key, week: info.week, year: info.year, weekStart: info.weekStart, weekEnd: info.weekEnd, txns: [] };
      weekMap[key].txns.push(t);
    }
    return Object.values(weekMap)
      .sort((a, b) => b.key.localeCompare(a.key))
      .map(wk => ({
        label: `Week ${wk.week}`,
        sublabel: weekRangeLabel(wk.weekStart, wk.weekEnd),
        key: wk.key,
        bas: computeBAS(wk.txns),
      }));
  }, [allTxns, selectedMonth]);

  // Count per category for filter bar
  const catCounts = useMemo(() => {
    const c = { ALL: displayTxns.length };
    for (const t of displayTxns) { const cat = t.category||'Expense'; c[cat] = (c[cat]||0)+1; }
    return c;
  }, [displayTxns]);

  // Count per account (sub-category) within selected category
  const accountCounts = useMemo(() => {
    const base = filterCat === 'ALL' ? displayTxns : displayTxns.filter(t => (t.category||'Expense') === filterCat);
    const c = { ALL: base.length };
    for (const t of base) { const acc = t.account||''; if (acc) c[acc] = (c[acc]||0)+1; }
    return c;
  }, [displayTxns, filterCat]);

  // Sub-category accounts available for the selected category
  const accountsInCat = useMemo(() => {
    if (filterCat === 'ALL') return [];
    return (ACCOUNTS[filterCat]?.items || []).filter(({ account }) => accountCounts[account] > 0);
  }, [filterCat, accountCounts]);

  const input = (extra={}) => ({
    width: '100%', padding: '6px 8px', fontSize: 12,
    border: '1px solid #d1d5db', borderRadius: 0,
    boxSizing: 'border-box', outline: 'none', ...extra,
  });

  return (
    <div>
      {/* ── Zoho-is-source banner ───────────────────────────────────────────
          Bank transactions now flow through Zoho Books' bank feed. This tab
          is read-only history + the KPI dashboard (Zoho-powered) + the
          bank↔Zoho reconciliation card. Pasting statements here (the old
          duplicated data entry) has been retired. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        background: '#F0FDF4', border: '1px solid #86EFAC', borderRadius: 10,
        padding: '10px 14px', marginBottom: 12,
      }}>
        <span style={{ fontSize: 18 }}>📊</span>
        <div style={{ fontSize: 12.5, color: '#14532D', lineHeight: 1.4 }}>
          <strong>Bank data now comes from Zoho Books.</strong> Categorise transactions in Zoho's
          bank feed — this tab is read-only history, the Zoho-powered KPI dashboard, and the
          bank ↔ Zoho reconciliation. You no longer paste statements here.
        </div>
      </div>

      {/* ── Bank account balances strip (4 fixed accounts) ──────────────────── */}
      <BankAccountsStrip
        accounts={bankAccounts}
        balances={bankBalances}
        filterAccountId={filterAccountId}
        setFilterAccountId={setFilterAccountId}
        onRematch={handleRematch}
        rematchBusy={rematchBusy}
      />

      {/* ── Dynamic financial dashboard (top, full width) ──────────────────── */}
      <DynamicDashboard
        allTxns={
          filterAccountId === 'ALL'
            ? allTxns
            : allTxns.filter(t => t.bank_account_id === parseInt(filterAccountId))
        }
        // After a one-click "Fix" in the reconciliation card, refresh the
        // whole panel so the local totals and Browse-All view both reflect
        // the new category.
        onTransactionUpdated={loadList}
      />

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>

      {/* ── LEFT PANEL — Saved Statements browser ───────────────────────────── */}
      <div style={{ width: 320, flexShrink: 0, background: '#fff', border: '1px solid #E8E8E8', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>

        {/* Header: dynamic title (reflects active account filter) + new btn */}
        {(() => {
          const activeAcct = filterAccountId === 'ALL'
            ? null
            : bankAccounts.find(a => String(a.id) === String(filterAccountId));
          const visibleStmts = filterAccountId === 'ALL'
            ? statements
            : statements.filter(s => s.bank_account_id === parseInt(filterAccountId));
          return (
            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: NAVY }}>
                  Saved Statements
                </div>
                <span style={{ fontSize: 11, color: '#94A3B8', fontWeight: 600 }}>{visibleStmts.length}</span>
              </div>
              <div style={{ fontSize: 11, color: '#64748B', marginBottom: 10 }}>
                {activeAcct ? `📂 ${activeAcct.name}` : 'All accounts'}
              </div>
              {/* "+ New Statement" (manual paste + categorise) retired — bank
                  data comes from Zoho's feed now. The saved-statements list and
                  Browse-all remain as read-only history. */}
              {/* Toggle: jump to a cross-statement transactions view with
                  its own category / sub-category / search / date filters. */}
              <button
                onClick={() => {
                  setViewMode(viewMode === 'browse' ? 'statement' : 'browse');
                  if (viewMode !== 'browse') {
                    // Entering browse mode — clear the per-statement selection
                    setSelectedId(null); setStatement(null); setTransactions([]);
                    setParsed(null);
                  } else {
                    // Leaving — drop the date range so the next entry isn't
                    // accidentally pre-filtered to the wrong period.
                    setBrowseFrom(''); setBrowseTo('');
                  }
                }}
                style={{
                  width: '100%', padding: '8px 10px', borderRadius: 8,
                  background: viewMode === 'browse' ? NAVY : '#fff',
                  color:      viewMode === 'browse' ? '#fff' : NAVY,
                  border: `2px solid ${NAVY}`,
                  fontWeight: 700, fontSize: 12, cursor: 'pointer', marginBottom: 8,
                }}
              >
                {viewMode === 'browse' ? '← Back to Statements' : '🔍 Browse all transactions'}
              </button>

              {/* Quick-period selectors: jump straight into Browse-All
                  with the matching date range pre-applied. Honours the
                  active bank-account card filter, so e.g. ACTS + Apr 2026
                  shows just that month for that account. */}
              {(() => {
                const visibleTxns = filterAccountId === 'ALL'
                  ? allTxns
                  : allTxns.filter(t => t.bank_account_id === parseInt(filterAccountId));
                if (visibleTxns.length === 0) return null;

                // Compute available months / FYs / years from the data
                const monthSet = new Set();
                const yearSet  = new Set();
                const fySet    = new Set();
                for (const t of visibleTxns) {
                  if (!t.txn_date) continue;
                  const [y, m] = t.txn_date.split('-').map(Number);
                  monthSet.add(`${y}-${String(m).padStart(2, '0')}`);
                  yearSet.add(y);
                  fySet.add(m >= 7 ? y + 1 : y); // FY end year
                }
                const months = [...monthSet].sort().reverse();
                const years  = [...yearSet].sort().reverse();
                const fys    = [...fySet].sort().reverse();

                // Open Browse-All with the given date range
                const openWith = (from, to) => {
                  setSelectedId(null); setStatement(null); setTransactions([]); setParsed(null);
                  setBrowseFrom(from); setBrowseTo(to);
                  setBrowseCat('ALL'); setBrowseAccount('ALL'); setBrowseSearch('');
                  setViewMode('browse');
                };

                // Range derivers
                const monthRange = (key) => {
                  const [y, m] = key.split('-').map(Number);
                  const last = new Date(y, m, 0).getDate();
                  return [`${key}-01`, `${key}-${String(last).padStart(2, '0')}`];
                };
                const fyRange = (endYear) => [`${endYear - 1}-07-01`, `${endYear}-06-30`];
                const yearRange = (y) => [`${y}-01-01`, `${y}-12-31`];

                const isActive = (from, to) => browseFrom === from && browseTo === to && viewMode === 'browse';

                const chipBase = (active) => ({
                  padding: '4px 10px', fontSize: 10.5, fontWeight: 700, borderRadius: 999,
                  border: active ? `2px solid ${NAVY}` : '1px solid #E5E7EB',
                  background: active ? NAVY : '#fff',
                  color: active ? '#fff' : '#475569',
                  cursor: 'pointer', whiteSpace: 'nowrap',
                });

                const monthLabel = (key) => {
                  const [y, m] = key.split('-').map(Number);
                  return `${MONTH_NAMES[m - 1].slice(0, 3)} ${y}`;
                };
                const fyLabel = (e) => `FY ${e - 1}–${String(e).slice(2)}`;

                return (
                  <div style={{
                    border: '1px dashed #CBD5E1', borderRadius: 8,
                    padding: 8, marginBottom: 8, background: '#FAFAF7',
                  }}>
                    <div style={{ fontSize: 9, fontWeight: 800, color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 4 }}>
                      Quick periods
                    </div>

                    {/* MONTH row */}
                    <div style={{ marginBottom: 4 }}>
                      <div style={{ fontSize: 9, color: '#94A3B8', fontWeight: 700, marginBottom: 2 }}>Month</div>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', maxHeight: 56, overflow: 'auto' }}>
                        {months.slice(0, 12).map(key => {
                          const [from, to] = monthRange(key);
                          return (
                            <button key={key} onClick={() => openWith(from, to)}
                              style={chipBase(isActive(from, to))}
                            >{monthLabel(key)}</button>
                          );
                        })}
                        {months.length > 12 && (
                          <span style={{ fontSize: 9, color: '#94A3B8', alignSelf: 'center', marginLeft: 4 }}>+{months.length - 12} more (use date picker)</span>
                        )}
                      </div>
                    </div>

                    {/* FY row */}
                    {fys.length > 0 && (
                      <div style={{ marginBottom: 4 }}>
                        <div style={{ fontSize: 9, color: '#94A3B8', fontWeight: 700, marginBottom: 2 }}>FY (Jul–Jun)</div>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {fys.map(e => {
                            const [from, to] = fyRange(e);
                            return (
                              <button key={e} onClick={() => openWith(from, to)}
                                style={chipBase(isActive(from, to))}
                              >{fyLabel(e)}</button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* YEAR row */}
                    {years.length > 0 && (
                      <div>
                        <div style={{ fontSize: 9, color: '#94A3B8', fontWeight: 700, marginBottom: 2 }}>Calendar year</div>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {years.map(y => {
                            const [from, to] = yearRange(y);
                            return (
                              <button key={y} onClick={() => openWith(from, to)}
                                style={chipBase(isActive(from, to))}
                              >{y}</button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
              {/* Search box — substring match on period_label / account name */}
              <div style={{ position: 'relative', marginBottom: 8 }}>
                <input
                  value={stmtSearch}
                  onChange={e => setStmtSearch(e.target.value)}
                  placeholder="Search week, date, account…"
                  style={input({ paddingLeft: 28, fontSize: 11 })}
                />
                <span style={{ position: 'absolute', left: 9, top: 7, fontSize: 12, color: '#94A3B8' }}>🔍</span>
                {stmtSearch && (
                  <button
                    onClick={() => setStmtSearch('')}
                    style={{
                      position: 'absolute', right: 6, top: 4, padding: '2px 6px',
                      background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8',
                      fontSize: 14, lineHeight: 1,
                    }}
                  >×</button>
                )}
              </div>
            </div>
          );
        })()}

        {/* Statements list — grouped by year, filtered by active account + search */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, maxHeight: 'calc(100vh - 320px)' }}>
          {listLoading && <div style={{ fontSize: 12, color: '#9ca3af', padding: '8px 0' }}>Loading…</div>}
          {!listLoading && statements.length === 0 && (
            <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic', padding: '8px 0' }}>No statements saved yet.</div>
          )}

          {(() => {
            // Apply account filter + search filter, then re-group by year so
            // the year counts reflect what's actually visible.
            const q = stmtSearch.trim().toLowerCase();
            const filtered = statements.filter(s => {
              if (filterAccountId !== 'ALL' && s.bank_account_id !== parseInt(filterAccountId)) return false;
              if (!q) return true;
              const hay = ((s.period_label || '') + ' ' + (s.bank_account_name || '')).toLowerCase();
              return hay.includes(q);
            });
            const byYear = groupStatementsByYear(filtered);
            if (byYear.length === 0 && !listLoading && statements.length > 0) {
              return (
                <div style={{ fontSize: 12, color: '#94A3B8', fontStyle: 'italic', padding: '12px 4px', textAlign: 'center' }}>
                  No statements match this filter.
                </div>
              );
            }
            return byYear.map(([yr, stmts]) => (
              <div key={yr} style={{ marginBottom: 8 }}>
                <button onClick={() => toggleYear(yr)} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', background: '#F1F5F9', border: 'none', borderRadius: 6,
                  padding: '6px 10px', cursor: 'pointer', marginBottom: 4,
                }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: NAVY }}>{yr}</span>
                  <span style={{ fontSize: 10, color: '#94A3B8', fontWeight: 700 }}>
                    {collapsedYears[yr] ? '▶' : '▼'} {stmts.length}
                  </span>
                </button>
                {!collapsedYears[yr] && stmts.map(s => (
                  <div key={s.id} onClick={() => loadStatement(s.id)} style={{
                    padding: '8px 10px', marginBottom: 4, marginLeft: 8, borderRadius: 6,
                    cursor: 'pointer',
                    background: selectedId === s.id ? NAVY : '#f9fafb',
                    border: selectedId === s.id ? `2px solid ${NAVY}` : '1px solid #E5E7EB',
                  }}>
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: selectedId === s.id ? '#fff' : NAVY, marginBottom: 2, lineHeight: 1.3 }}>
                      {s.period_label}
                    </div>
                    <div style={{ fontSize: 10, color: selectedId === s.id ? '#cbd5e1' : '#64748b', marginBottom: 2, fontWeight: 600 }}>
                      {s.bank_account_name ? `📂 ${s.bank_account_name}` : <span style={{ color: '#dc2626' }}>⚠ No account</span>}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 10, color: selectedId === s.id ? '#94a3b8' : '#9ca3af' }}>
                        {s.transaction_count} txns
                      </span>
                      <button
                        onClick={e => { e.stopPropagation(); handleDelete(s.id); }}
                        style={{ fontSize: 10, color: selectedId === s.id ? '#fecaca' : '#ef4444', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                      >Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            ));
          })()}
        </div>
      </div>

      {/* ── MAIN TABLE ──────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Browse-all-transactions view — independent of single-statement viewer. */}
        {viewMode === 'browse' && (
          <BrowseAllTransactions
            allTxns={allTxns}
            bankAccounts={bankAccounts}
            bankBalances={bankBalances}
            filterAccountId={filterAccountId}
            setFilterAccountId={setFilterAccountId}
            browseCat={browseCat}             setBrowseCat={setBrowseCat}
            browseAccount={browseAccount}     setBrowseAccount={setBrowseAccount}
            browseSearch={browseSearch}       setBrowseSearch={setBrowseSearch}
            browseFrom={browseFrom}           setBrowseFrom={setBrowseFrom}
            browseTo={browseTo}               setBrowseTo={setBrowseTo}
            browseSavingId={browseSavingId}
            onUpdate={async (txn, patch) => {
              // Reuse the unified updater so per-statement viewer and
              // Browse-All stay perfectly in sync, and transfer pairs
              // propagate visibly without a refresh.
              setBrowseSavingId(txn.id);
              try { await applyTxnUpdate(txn, patch); }
              finally { setBrowseSavingId(null); }
            }}
            onSplit={(txn) => setSplitTxn(txn)}
          />
        )}

        {viewMode === 'statement' && !statement && !parsed && (
          <div style={{ background: '#fff', border: '1px solid #E8E8E8', borderRadius: 0, padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
            Paste a bank statement and click <strong>Parse</strong>, or select a saved statement.
          </div>
        )}

        {viewMode === 'statement' && (statement || parsed) && (
          <div style={{ background: '#fff', border: '1px solid #E8E8E8', borderRadius: 0, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>

            {/* Header */}
            <div style={{ background: NAVY, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ color: '#fff', fontWeight: 800, fontSize: 14, flex: 1 }}>
                {statement ? statement.period_label : `Preview — ${parsed.length} transactions`}
              </div>
              <span style={{ fontSize: 11, color: '#94a3b8' }}>
                {filteredTxns.length}{filterCat !== 'ALL' ? ` of ${displayTxns.length}` : ''} transactions
              </span>
              {parsed && <span style={{ fontSize: 11, color: YELLOW, fontWeight: 600 }}>Not saved yet — click Save</span>}
            </div>

            {/* Category filter bar */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '10px 14px', borderBottom: '1px solid #e5e7eb', background: '#f9fafb', alignItems: 'center' }}>
              {['ALL', ...Object.keys(ACCOUNTS)].map(cat => {
                const count = catCounts[cat] || 0;
                if (count === 0 && cat !== 'ALL') return null;
                const isActive = filterCat === cat;
                const color = cat === 'ALL' ? NAVY : (ACCOUNTS[cat]?.color || '#6b7280');
                return (
                  <button key={cat} onClick={() => { setFilterCat(cat); setFilterAccount('ALL'); }} style={{
                    padding: '4px 12px', fontSize: 11, fontWeight: 700, borderRadius: 0, cursor: 'pointer', whiteSpace: 'nowrap',
                    border: isActive ? `2px solid ${color}` : '1px solid #e5e7eb',
                    background: isActive ? color : '#fff',
                    color: isActive ? '#fff' : '#6b7280',
                  }}>
                    {cat === 'ALL' ? 'All' : cat} <span style={{ opacity: 0.8 }}>({cat === 'ALL' ? catCounts.ALL : count})</span>
                  </button>
                );
              })}
            </div>

            {/* Sub-category filter bar — shown when a category is selected */}
            {accountsInCat.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, padding: '7px 14px', borderBottom: '1px solid #e5e7eb', background: '#f3f4f6', alignItems: 'center' }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', marginRight: 4 }}>Sub-category:</span>
                {/* ALL sub-category */}
                <button onClick={() => setFilterAccount('ALL')} style={{
                  padding: '3px 10px', fontSize: 11, fontWeight: 700, borderRadius: 0, cursor: 'pointer', whiteSpace: 'nowrap',
                  border: filterAccount === 'ALL' ? `2px solid ${ACCOUNTS[filterCat]?.color || '#6b7280'}` : '1px solid #e5e7eb',
                  background: filterAccount === 'ALL' ? (ACCOUNTS[filterCat]?.color || '#6b7280') : '#fff',
                  color: filterAccount === 'ALL' ? '#fff' : '#6b7280',
                }}>
                  All <span style={{ opacity: 0.8 }}>({accountCounts.ALL})</span>
                </button>
                {accountsInCat.map(({ account }) => {
                  const isActive = filterAccount === account;
                  const catColor = ACCOUNTS[filterCat]?.color || '#6b7280';
                  const accentColor = ACCOUNT_ROW_COLORS[account]?.border || catColor;
                  return (
                    <button key={account} onClick={() => setFilterAccount(account)} style={{
                      padding: '3px 10px', fontSize: 11, fontWeight: 700, borderRadius: 0, cursor: 'pointer', whiteSpace: 'nowrap',
                      border: isActive ? `2px solid ${accentColor}` : '1px solid #e5e7eb',
                      background: isActive ? accentColor : '#fff',
                      color: isActive ? '#fff' : '#6b7280',
                    }}>
                      {account} <span style={{ opacity: 0.8 }}>({accountCounts[account] || 0})</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Table */}
            <div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, tableLayout: 'auto' }}>
                <colgroup>
                  <col style={{ width: 72 }} />
                  <col />
                  <col style={{ width: 88 }} />
                  <col style={{ width: 88 }} />
                  <col style={{ width: 160 }} />
                  <col style={{ width: 120 }} />
                </colgroup>
                <thead>
                  <tr style={{ background: '#f4f8fa' }}>
                    {[{l:'Date',a:'left'},{l:'Description',a:'left'},{l:'Debit',a:'right'},{l:'Credit',a:'right'},{l:'Account / GST',a:'left'},{l:'Notes',a:'left'}].map(h => (
                      <th key={h.l} style={{ padding: '7px 8px', textAlign: h.a, fontWeight: 700, fontSize: 11, color: '#6b7280', borderBottom: '2px solid #e5e7eb', whiteSpace: 'nowrap' }}>{h.l}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {monthGroups.map(mo => {
                    const moCollapsed  = collapsedMonths[mo.monthKey];
                    const moDebit  = mo.weeks.flatMap(w => w.txns).reduce((s,t) => s + (Number(t.debit) ||0), 0);
                    const moCredit = mo.weeks.flatMap(w => w.txns).reduce((s,t) => s + (Number(t.credit)||0), 0);
                    const moCount  = mo.weeks.reduce((s,w) => s + w.txns.length, 0);
                    const moLabel  = `${MONTH_NAMES[mo.month - 1]} ${mo.year}`;
                    return (
                      <React.Fragment key={mo.monthKey}>
                        {/* ── Month header ── */}
                        <tr onClick={() => toggleMonth(mo.monthKey)} style={{ background: NAVY, cursor: 'pointer', userSelect: 'none' }}>
                          <td colSpan={2} style={{ padding: '7px 10px', fontWeight: 900, fontSize: 12, color: '#fff', whiteSpace: 'nowrap' }}>
                            <span style={{ marginRight: 6, fontSize: 10, color: YELLOW }}>{moCollapsed ? '▶' : '▼'}</span>
                            {moLabel}
                          </td>
                          <td style={{ padding: '7px 8px', textAlign: 'right', fontSize: 11, fontWeight: 700, color: '#fca5a5', whiteSpace: 'nowrap' }}>
                            {moDebit > 0 ? `-$${fmt(moDebit)}` : ''}
                          </td>
                          <td style={{ padding: '7px 8px', textAlign: 'right', fontSize: 11, fontWeight: 700, color: '#86efac', whiteSpace: 'nowrap' }}>
                            {moCredit > 0 ? `+$${fmt(moCredit)}` : ''}
                          </td>
                          <td colSpan={2} style={{ padding: '7px 8px', fontSize: 10, color: '#94a3b8', textAlign: 'right' }}>
                            {moCount} transactions
                          </td>
                        </tr>

                        {!moCollapsed && mo.weeks.map(grp => {
                          const wkCollapsed = collapsedWeeks[grp.key];
                          const grpDebit  = grp.txns.reduce((s,t) => s + (Number(t.debit) ||0), 0);
                          const grpCredit = grp.txns.reduce((s,t) => s + (Number(t.credit)||0), 0);
                          return (
                            <React.Fragment key={grp.key}>
                              {/* ── Week sub-header ── */}
                              <tr onClick={() => toggleWeek(grp.key)} style={{ background: '#EEF3F7', cursor: 'pointer', userSelect: 'none' }}>
                                <td colSpan={2} style={{ padding: '5px 8px 5px 22px', fontWeight: 700, fontSize: 11, color: NAVY, borderBottom: '1px solid #D0D8E0', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                                  <span style={{ marginRight: 6, fontSize: 10, color: '#9ca3af' }}>{wkCollapsed ? '▶' : '▼'}</span>
                                  Week {grp.week}
                                  <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 400, color: '#6b7280' }}>{weekRangeLabel(grp.weekStart, grp.weekEnd)}</span>
                                </td>
                                <td style={{ padding: '5px 8px', textAlign: 'right', borderBottom: '1px solid #D0D8E0', fontSize: 11, fontWeight: 700, color: '#dc2626', whiteSpace: 'nowrap' }}>
                                  {grpDebit > 0 ? `-$${fmt(grpDebit)}` : ''}
                                </td>
                                <td style={{ padding: '5px 8px', textAlign: 'right', borderBottom: '1px solid #D0D8E0', fontSize: 11, fontWeight: 700, color: '#16a34a', whiteSpace: 'nowrap' }}>
                                  {grpCredit > 0 ? `+$${fmt(grpCredit)}` : ''}
                                </td>
                                <td colSpan={2} style={{ padding: '5px 8px', borderBottom: '1px solid #D0D8E0', fontSize: 10, color: '#9ca3af', textAlign: 'right' }}>
                                  {grp.txns.length} txns
                                </td>
                              </tr>

                              {/* ── Transaction rows — grouped by date ── */}
                              {!wkCollapsed && (() => {
                                // Group transactions by date within the week
                                const byDate = {};
                                grp.txns.forEach(t => {
                                  const dk = t.txn_date || '';
                                  if (!byDate[dk]) byDate[dk] = [];
                                  byDate[dk].push(t);
                                });
                                const dateKeys = Object.keys(byDate).sort((a,b) => b.localeCompare(a));
                                const DAY_NAMES = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
                                let globalIdx = 0;
                                return dateKeys.map(dk => {
                                  const txnsForDate = byDate[dk];
                                  // Format: "Wed 08 Apr 2026"
                                  const dateObj = dk ? new Date(dk + 'T00:00:00') : null;
                                  const dayLabel = dateObj
                                    ? `${DAY_NAMES[dateObj.getDay()]} ${fmtDate(dk)}`
                                    : '—';
                                  return (
                                    <React.Fragment key={dk}>
                                      {/* Date separator row */}
                                      <tr style={{ background: '#f0f4f8' }}>
                                        <td colSpan={6} style={{ padding: '5px 8px 4px 32px', fontWeight: 700, fontSize: 11, color: NAVY, borderBottom: '1px solid #dde3ea', borderTop: '1px solid #dde3ea', letterSpacing: 0.3 }}>
                                          {dayLabel}
                                        </td>
                                      </tr>
                                      {txnsForDate.map(t => {
                                        const realIdx  = displayTxns.indexOf(t);
                                        const isParsed = !!parsed;
                                        const catColor  = ACCOUNTS[t.category]?.color || '#9ca3af';
                                        const rowAccent = ACCOUNT_ROW_COLORS[t.account];
                                        const rowBg = rowAccent ? rowAccent.bg : (globalIdx++ % 2 === 0 ? '#fff' : '#fafafa');
                                        return (
                                          <tr key={t.id || realIdx} style={{ background: rowBg, borderLeft: rowAccent ? `3px solid ${rowAccent.border}` : undefined }}>
                                            <td style={{ padding: '4px 8px 4px 28px', borderBottom: '1px solid #f3f4f6', color: '#9ca3af', fontSize: 10 }}></td>
                                            <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6', wordBreak: 'normal', overflowWrap: 'anywhere' }}>
                                              <div style={{ fontSize: 11, color: '#1f2937', whiteSpace: 'normal', wordBreak: 'normal', overflowWrap: 'anywhere' }}>{t.description}</div>
                                              <div style={{ fontSize: 10, color: catColor, fontWeight: 600 }}>{t.category}</div>
                                            </td>
                                            <td style={{ padding: '4px 8px', textAlign: 'right', borderBottom: '1px solid #f3f4f6', fontWeight: t.debit > 0 ? 700 : 400, color: t.debit > 0 ? '#dc2626' : '#d1d5db', whiteSpace: 'nowrap', fontSize: 11 }}>
                                              {t.debit > 0 ? `$${fmt(t.debit)}` : '—'}
                                            </td>
                                            <td style={{ padding: '4px 8px', textAlign: 'right', borderBottom: '1px solid #f3f4f6', fontWeight: t.credit > 0 ? 700 : 400, color: t.credit > 0 ? '#16a34a' : '#d1d5db', whiteSpace: 'nowrap', fontSize: 11 }}>
                                              {t.credit > 0 ? `$${fmt(t.credit)}` : '—'}
                                            </td>
                                            <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6' }}>
                                              <select
                                                value={t.account || 'Other Expenses'}
                                                onChange={e => handleAccountChange(t, realIdx, e.target.value, isParsed)}
                                                style={{ fontSize: 11, padding: '2px 4px', borderRadius: 0, border: `1px solid ${catColor}40`, background: catColor+'18', color: catColor, fontWeight: 700, cursor: 'pointer', outline: 'none', width: '100%' }}
                                              >
                                                {Object.entries(ACCOUNTS).map(([cat, { items }]) => (
                                                  <optgroup key={cat} label={`── ${cat}`}>
                                                    {items.map(({ account }) => (
                                                      <option key={account} value={account}>{account}</option>
                                                    ))}
                                                  </optgroup>
                                                ))}
                                              </select>
                                              <button
                                                onClick={() => handleGstToggle(t, realIdx, isParsed)}
                                                title={t.has_gst ? 'GST included — click to remove' : 'No GST — click to add'}
                                                style={{ marginTop: 2, padding: '1px 6px', fontSize: 10, fontWeight: 700, borderRadius: 0, border: 'none', cursor: 'pointer', background: t.has_gst ? '#d1fae5' : '#f3f4f6', color: t.has_gst ? '#065f46' : '#9ca3af' }}>
                                                {t.has_gst ? '✓ GST' : 'No GST'}
                                              </button>
                                            </td>
                                            <td style={{ padding: '4px 8px', borderBottom: '1px solid #f3f4f6' }}>
                                              <input
                                                defaultValue={t.notes || ''}
                                                onChange={e => { if (isParsed) updateParsed(realIdx, { notes: e.target.value }); else setTransactions(prev => prev.map(x => x.id === t.id ? {...x, notes: e.target.value} : x)); }}
                                                onBlur={e => { if (!isParsed) saveUpdate(t, { notes: e.target.value }); }}
                                                placeholder="notes..."
                                                style={{ fontSize: 11, padding: '2px 5px', border: '1px solid #e5e7eb', borderRadius: 0, width: '100%', outline: 'none', boxSizing: 'border-box' }}
                                              />
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </React.Fragment>
                                  );
                                });
                              })()}
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Financial Summary sidebar removed at boss's request — the dashboard
          at the top already shows period KPIs + BAS, and the statement
          viewer now expands to fill the freed column. */}
    </div>

    {/* ── New Statement modal ─────────────────────────────────────────────── */}
    {/* Split modal — when set, shows the itemise-into-multiple-categories editor */}
    {splitTxn && (
      <SplitModal
        txn={splitTxn}
        onClose={() => setSplitTxn(null)}
        onSaved={async () => {
          // Refetch allTxns so the splits we just saved show up
          try {
            const fresh = await api.getAllTransactions();
            setAllTxns(fresh);
          } catch (e) { console.error(e); }
          setSplitTxn(null);
        }}
      />
    )}

    {showNewModal && (
      <div
        onClick={() => !saving && setShowNewModal(false)}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 700, padding: 16,
        }}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{
            background: '#fff', borderRadius: 12, width: '100%', maxWidth: 600,
            maxHeight: '90vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          }}
        >
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: NAVY }}>
              {parsed ? `Review & Save — ${parsed.length} transactions` : 'New Bank Statement'}
            </div>
            <button onClick={() => !saving && setShowNewModal(false)} disabled={saving}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: 20, padding: 4 }}>×</button>
          </div>

          <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 4 }}>Bank Account *</label>
                <select value={pickedAccountId} onChange={e => setPickedAccountId(e.target.value)} style={input({ cursor: 'pointer' })}>
                  <option value="">— pick account —</option>
                  {/* Fallback to bankBalances when bankAccounts didn't load —
                      same trick BankAccountsStrip uses so the dropdown is
                      never empty as long as one of the two endpoints works. */}
                  {(bankAccounts.length > 0 ? bankAccounts : bankBalances)
                    .map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>Period Label *</span>
                  {(() => {
                    // Build the same label format the import script uses:
                    //   "ACTS · Week 19 / 2026 (04/05–10/05)"
                    // Driven by the picked account's code + the From date.
                    // Only render the "Auto" button when we have enough to
                    // generate a sensible suggestion.
                    const accountList = bankAccounts.length > 0 ? bankAccounts : bankBalances;
                    const acct = accountList.find(a => String(a.id) === String(pickedAccountId));
                    if (!acct || !weekStart) return null;
                    const suggest = (() => {
                      const w = getISOWeekInfo(weekStart);
                      const dms = weekStart.slice(8, 10) + '/' + weekStart.slice(5, 7);
                      const endIso = weekEnd || weekStart;
                      const dme = endIso.slice(8, 10) + '/' + endIso.slice(5, 7);
                      return `${acct.code} · Week ${w.week} / ${w.year} (${dms}–${dme})`;
                    })();
                    const isAlready = periodLabel.trim() === suggest;
                    return (
                      <button
                        type="button"
                        onClick={() => setPeriodLabel(suggest)}
                        disabled={isAlready}
                        title="Fill label from account + dates"
                        style={{
                          padding: '2px 8px', fontSize: 10, fontWeight: 700,
                          border: '1px solid #CBD5E1',
                          background: isAlready ? '#F0FDF4' : '#fff',
                          color: isAlready ? '#15803D' : '#1E3A4A',
                          borderRadius: 4, cursor: isAlready ? 'default' : 'pointer',
                          marginLeft: 8,
                        }}
                      >
                        {isAlready ? '✓ Auto' : '✨ Auto-fill'}
                      </button>
                    );
                  })()}
                </label>
                <input
                  value={periodLabel}
                  onChange={e => { setPeriodLabel(e.target.value); setLabelEdited(true); }}
                  placeholder="Pick account + dates, then click ✨ Auto-fill"
                  style={input()}
                />
                {/* Override button: re-enable auto mode if the boss wants the
                    suggestion back after typing manually. */}
                {labelEdited && pickedAccountId && weekStart && (
                  <button
                    type="button"
                    onClick={() => { setLabelEdited(false); setPeriodLabel(''); }}
                    style={{ marginTop: 4, padding: '2px 8px', fontSize: 10, border: 'none', background: 'none', color: '#1E40AF', cursor: 'pointer' }}
                  >
                    ↻ Reset to auto-fill
                  </button>
                )}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 4 }}>From</label>
                <input type="date" value={weekStart} onChange={e => setWeekStart(e.target.value)} style={input()} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 4 }}>To</label>
                <input type="date" value={weekEnd} onChange={e => setWeekEnd(e.target.value)} style={input()} />
              </div>
            </div>

            <label style={{ fontSize: 11, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 4 }}>
              Paste Bank Statement
              <span style={{ fontWeight: 500, color: '#94A3B8', marginLeft: 6 }}>(date ‹tab› amount ‹tab› description ‹tab› balance)</span>
            </label>
            <textarea
              value={rawText}
              onChange={e => { setRawText(e.target.value); setParsed(null); setParseError(''); }}
              placeholder={'08/04/2026\t-1209.80\tTransfer To CBA NetBank\t10359.95\n08/04/2026\t-339.63\tTransfer To Acts TAX\t11569.75'}
              rows={9}
              style={input({ fontFamily: 'monospace', fontSize: 11, resize: 'vertical' })}
            />
            {parseError && <div style={{ fontSize: 12, color: '#dc2626', marginTop: 6 }}>{parseError}</div>}

            {parsed && (
              <div style={{
                marginTop: 12, padding: '10px 12px', background: '#F0FDF4',
                border: '1px solid #BBF7D0', borderRadius: 8, fontSize: 12, color: '#15803D',
              }}>
                ✓ Parsed {parsed.length} transaction{parsed.length === 1 ? '' : 's'} — review then click <strong>Save</strong> below.
              </div>
            )}
          </div>

          <div style={{ padding: '14px 20px', borderTop: '1px solid #E5E7EB', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={() => !saving && setShowNewModal(false)} disabled={saving}
              style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #E5E7EB', background: '#fff', color: '#475569', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              Cancel
            </button>
            {!parsed && (
              <button onClick={handleParse}
                style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: YELLOW, color: NAVY, fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>
                Parse
              </button>
            )}
            {parsed && (
              <button
                onClick={async () => { await handleSave(); setShowNewModal(false); }}
                disabled={saving}
                style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: NAVY, color: '#fff', fontWeight: 800, fontSize: 13, cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Saving…' : `Save (${parsed.length})`}
              </button>
            )}
          </div>
        </div>
      </div>
    )}
    </div>
  );
}

// ── Small helper components ────────────────────────────────────────────────
function Section({ title, color, children }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
        <div style={{ width: 8, height: 8, borderRadius: 0, background: color }} />
        {title}
      </div>
      {children}
    </div>
  );
}

function Line({ label, value, sub, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '3px 0', borderBottom: '1px solid #f3f4f6' }}>
      <span style={{ fontSize: 11, color: '#374151', flex: 1, paddingRight: 4 }}>{label}</span>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color }}>{value}</div>
        {sub && <div style={{ fontSize: 10, color: '#9ca3af' }}>{sub}</div>}
      </div>
    </div>
  );
}

function Total({ label, value, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, paddingTop: 4, borderTop: `1px solid ${color}30` }}>
      <span style={{ fontSize: 11, fontWeight: 700, color }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 800, color }}>{value}</span>
    </div>
  );
}

// ── SummaryCard: one period card (monthly or weekly) ──────────────────────────
function SummaryCard({ label, sublabel, bas, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const CARD_NAVY   = '#1e3a4a';
  const CARD_YELLOW = '#FFD54F';

  // Defensive: handle undefined bas
  const b = bas || { income: {}, expense: {}, transfer: {}, totalIncomeEx: 0, totalExpense: 0, netProfit: 0, netGst: 0, gstCollected: 0, gstCredits: 0 };

  const transferEntries = Object.entries(b.transfer || {});
  const totalIn  = transferEntries.reduce((s, [, v]) => s + (Number(v && v.in)  || 0), 0);
  const totalOut = transferEntries.reduce((s, [, v]) => s + (Number(v && v.out) || 0), 0);
  const netTransfer = totalIn - totalOut;
  const cashFlow    = (b.netProfit || 0) + netTransfer;

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 0, overflow: 'hidden' }}>

      {/* ── Header (always visible, click to expand) ── */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{ background: CARD_NAVY, padding: '7px 10px', display: 'flex', alignItems: 'center', cursor: 'pointer', gap: 6 }}
      >
        {/* Arrow */}
        <span style={{ fontSize: 9, color: '#94a3b8', flexShrink: 0 }}>{open ? '▼' : '▶'}</span>
        {/* Label — takes available space */}
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</div>
          {sublabel && <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 1 }}>{sublabel}</div>}
        </div>
        {/* Amounts — right side, fixed width */}
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 900, color: (b.netProfit || 0) >= 0 ? CARD_YELLOW : '#fca5a5' }}>
            ${fmt(b.netProfit)}
          </div>
          {transferEntries.length > 0 && (
            <div style={{ fontSize: 9, color: cashFlow >= 0 ? '#86efac' : '#fca5a5' }}>
              cash {cashFlow >= 0 ? '+' : ''}${fmt(cashFlow)}
            </div>
          )}
        </div>
      </div>

      {/* ── Body (expandable) ── */}
      {open && (
        <div style={{ padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 3 }}>

          {/* INCOME */}
          {b.totalIncomeEx > 0 && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: ACCOUNTS.Income.color, textTransform: 'uppercase' }}>Income</span>
                <span style={{ fontSize: 11, fontWeight: 800, color: ACCOUNTS.Income.color }}>${fmt(b.totalIncomeEx)}</span>
              </div>
              {Object.entries(b.income).map(([acc, v]) => (
                <div key={acc} style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: 8 }}>
                  <span style={{ fontSize: 10, color: '#6b7280', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{acc}</span>
                  <span style={{ fontSize: 10, fontWeight: 600, color: '#374151' }}>${fmt(v.ex_gst)}</span>
                </div>
              ))}
            </div>
          )}

          {/* EXPENSES */}
          {b.totalExpense > 0 && (
            <div style={{ marginTop: 4 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: ACCOUNTS.Expense.color, textTransform: 'uppercase' }}>Expenses</span>
                <span style={{ fontSize: 11, fontWeight: 800, color: ACCOUNTS.Expense.color }}>-${fmt(b.totalExpense)}</span>
              </div>
              {Object.entries(b.expense).map(([acc, v]) => (
                <div key={acc} style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: 8 }}>
                  <span style={{ fontSize: 10, color: '#6b7280', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{acc}</span>
                  <span style={{ fontSize: 10, fontWeight: 600, color: '#374151' }}>${fmt(v.amount)}</span>
                </div>
              ))}
            </div>
          )}

          {/* TRANSFERS */}
          {transferEntries.length > 0 && (
            <div style={{ marginTop: 4, paddingTop: 4, borderTop: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Transfers</span>
                <span style={{ fontSize: 10, fontWeight: 800, color: netTransfer >= 0 ? '#16a34a' : '#6b7280' }}>
                  {netTransfer >= 0 ? '+' : '-'}${fmt(Math.abs(netTransfer))}
                </span>
              </div>
              {transferEntries.map(([acc, v]) => (
                <div key={acc} style={{ paddingLeft: 8, marginBottom: 3 }}>
                  <span style={{ fontSize: 10, color: '#374151', fontWeight: 600 }}>{acc}</span>
                  <div style={{ display: 'flex', gap: 10, paddingLeft: 6 }}>
                    {v.in  > 0 && <span style={{ fontSize: 10, color: '#16a34a', fontWeight: 600 }}>↓ in ${fmt(v.in)}</span>}
                    {v.out > 0 && <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600 }}>↑ out ${fmt(v.out)}</span>}
                  </div>
                </div>
              ))}
              {/* Cash flow strip */}
              <div style={{ marginTop: 4, padding: '4px 6px', background: cashFlow >= 0 ? '#f0fdf4' : '#fef2f2', borderRadius: 0, display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: '#374151' }}>Cash Flow</span>
                <span style={{ fontSize: 11, fontWeight: 900, color: cashFlow >= 0 ? '#16a34a' : '#dc2626' }}>
                  {cashFlow >= 0 ? '+' : ''}${fmt(cashFlow)}
                </span>
              </div>
            </div>
          )}

          {/* GST */}
          <div style={{ marginTop: 4, paddingTop: 4, borderTop: '1px dashed #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 10, color: '#9ca3af' }}>GST payable</span>
            <span style={{ fontSize: 10, fontWeight: 700, color: b.netGst >= 0 ? '#dc2626' : '#16a34a' }}>
              ${fmt(Math.abs(b.netGst))}{b.netGst < 0 ? ' refund' : ''}
            </span>
          </div>

        </div>
      )}
    </div>
  );
}
